/**
 * TimelineEditor — DAW-style horizontal timeline editor for the Timeline
 * family (Action / Song / Genre). Same editor surface for all three tiers
 * per DEC-030.
 *
 * M1.5 scope (2026-07-01):
 *   - Library list (left) grouped by tier — unchanged from M1
 *   - Selected timeline detail (right) is now a horizontal DAW grid:
 *       time on X (bar/beat ruled)
 *       lanes stacked vertically
 *       sections drawn as colored background bands (Song/Genre only —
 *         Action Timelines are one-shot chains, sections aren't meaningful)
 *   - Palette panel (far right) lists every fireable item (Button, Preset
 *     Change, Snapshot Change, Stream Deck - see `PaletteFireableItem`)
 *     across every page of the active project. Draggable onto a lane.
 *     Widened 2026-08-15 from Button-only (DEC-153's originally-flagged
 *     gap); still excludes per-cell kinds (Transpose/Capo/WavesAutotune)
 *     and continuous-value kinds (Fader/XYPad/Mixer/Radial/Lfo) - those
 *     need a step shape that doesn't exist yet, not just a palette entry.
 *   - Drop = insert a ButtonRefStep at the snapped bar/beat position on the
 *     drop target lane. Beat snap default. Auto-route: dropping outside
 *     an explicit lane routes to the first MIDI lane by default.
 *   - "+ MIDI lane" / "+ DMX lane" / "+ OSC lane" / "+ Marker lane" gutter
 *     buttons for stacking simultaneous events.
 *   - Preview-only playhead (spacebar toggles play; visual sweep only, no
 *     dispatch). Firing lands with the OPBridge opbridge-timelines crate.
 *
 * Interaction model:
 *   Sidebar drag → drop on lane row → step inserted at snapped position.
 *   Right-click a placed step block = remove.
 *   Delete-key on selected step block = remove.
 *   Section names editable via the sections list under the grid (Song/Genre).
 *   Action Timelines get a bare "length in bars" number input instead.
 *
 * Chrome unchanged from M1: full-screen surface (DEC-030); Esc closes.
 */

import { createSignal, createMemo, createEffect, on, onCleanup, onMount, For, Index, Show, type Accessor } from 'solid-js';
import { STAGE_COLORS } from '../lighting/palette.ts';
import { sampleColorLane } from '../timeline/colorSample.ts';
import { Portal } from 'solid-js/web';
import type {
  Timeline,
  TimelineTier,
  TimelineLane,
  TimelineLaneKind,
  TimelineStep,
  ButtonRefStep,
  ColorMarkerStep,
  TargetValueStep,
  ValuePointCurve,
  ColorInterpolationSpace,
  HuePathDirection,
} from '@opbuttons/schema';
import { isButtonRefStep, isColorMarkerStep, isTargetValueStep, timelineLoops, timelineLeadIn } from '@opbuttons/schema';
import { playheadAt } from '../timeline/playhead.ts';
import {
  selection,
  isSelected,
  setSelection,
  clearSelection,
  addToSelection,
  toggleInSelection,
  pruneSelection,
  selKey,
  parseSelKey,
  writeClipboard,
  readClipboard,
  hasClipboard,
  marqueeBarRange,
  setMarqueeBarRange,
  CLIPBOARD_VERSION,
  type ClipboardPayload,
  type ClipboardEntry,
} from '../timeline/selection.ts';
import {
  timelines,
  createTimeline,
  duplicateTimeline,
  renameTimeline,
  addSection,
  renameSection,
  commitSectionName,
  commitTimelineName,
  commitLaneName,
  setSectionLength,
  removeSection,
  addLane,
  renameLane,
  removeLane,
  duplicateLane,
  setTimelineLoop,
  addDmxFixtureLane,
  addStep,
  removeStep,
  moveStep,
  moveStepToLane,
  setStepDuration,
  placeStep,
  makeButtonRefStep,
  makeTargetValueStep,
  makeColorMarkerStep,
  setColorMarkerColor,
  setColorMarkerCurve,
  setColorMarkerSpace,
  setColorMarkerPath,
  removeSteps,
  placeStepsBulk,
  stepEndBars,
  duplicateStep,
  beginBatch,
  endBatch,
  undo,
  redo,
  findFirstLaneOfKind,
  closeTimelineEditor,
  switchTimelineEditor,
  timelineEditorTargetId,
  selectTimelineInEditor,
  timelinesInActiveProject,
} from '../timeline/store.ts';
import { announceHistoryResult } from '../undo/history.ts';
import { pagesOfActive, hueOf, HUE_VALUES } from '../layout/projectStore.ts';
import { barPosition } from '../settings/store.ts';
import type { ButtonItem, Page, PresetChangeItem, SnapshotChangeItem, StreamDeckItem } from '../layout/projectStore.ts';
import { helixSlotLabel } from './PresetChange.tsx';
import { fixtures } from '../fixtures/store.ts';
import type { FixtureInstance } from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { pushToast } from '../ui/toast.ts';
import { deleteTimelineFromRig } from '../timeline/deleteTimeline.ts';
import { LIVE_TARGETS, getLiveTarget } from '../liveState/registry.ts';
import {
  stopTimelineAction,
  summarizeTimelineDispatch, summarizeTimelinePush, notLiveToastMessage,
  timelineHomeTarget, timelineStepWarnings,
} from '../timeline/bridgeSync.ts';
import {
  scheduleTimelineSync, notePushStarted, pushTimelineParts, applyPartsOutcome,
  sweepPendingTimelineDeletes,
} from '../timeline/autoSync.ts';
import { launchTimelineOnBridge } from '../timeline/launch.ts';
import { followTransport, transportRun, transportBar, noteTransportLaunch, noteTransportStop } from '../timeline/transport.ts';
import { TimelinePlaysOnPill } from './TimelinePlaysOnPill.tsx';
import { plainFireReason, type FireResult } from '../opbridge/dispatch.ts';
import { TimelineSyncPill } from './TimelineSyncPill.tsx';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';

const TIER_ORDER: TimelineTier[] = ['action', 'song', 'genre'];
const TIER_LABEL: Record<TimelineTier, string> = {
  action: 'ACTION',
  song: 'SONG',
  genre: 'GENRE',
};
const TIER_HINT: Record<TimelineTier, string> = {
  action: 'One-shot chains fired from a button. Short, typically one section.',
  song: 'A song\'s arrangement. Sections + jumps. Started explicitly.',
  // Plan 3.4 (2026-10-01): OPBridge has never had a Genre runtime, so the
  // tier is hidden. The group shows only to keep timelines made before this
  // reachable, and it says why it cannot make new ones.
  genre: 'OPBridge can\'t play Genre timelines yet. These are kept, and will play once it can.',
};
/** Tiers you can make new timelines in. Genre comes back when it can play. */
const CREATABLE_TIERS: ReadonlySet<TimelineTier> = new Set(['action', 'song']);

/** Pixels per bar for the horizontal grid at zoom = 1. Roughly one bar
 *  ≈ a comfortable finger target; adjust by zoom range below. */
const PX_PER_BAR_BASE = 96;
/** Row height for each lane. */
const LANE_ROW_H = 56;
/** Height of the ruler strip above the grid. */
const RULER_H = 28;
/** Left-side gutter width holding lane labels + delete controls. */
const LANE_GUTTER_W = 140;

/** Snap position (in fractional bars) to the nearest beat.
 *  4/4 assumed for the editor grid (matches the informational signature
 *  default seeded on new timelines). */
function snapToBeat(positionBars: number, sigNum = 4): number {
  const beatsPerBar = Math.max(1, sigNum);
  const beats = positionBars * beatsPerBar;
  const snapped = Math.round(beats);
  return snapped / beatsPerBar;
}

/** Convert a bar position to a pixel offset that renders on a whole
 *  device pixel. Everything anchored to a beat (grid ticks, blocks,
 *  markers, ghost, playhead, drop preview) uses this so sub-pixel
 *  rounding doesn't cause visible drift between element sizes. */
function pxAt(positionBars: number, pxPerBar: number): number {
  return Math.round(positionBars * pxPerBar);
}

/** Item kinds the Timeline palette can offer as a `buttonRef` drag source -
 *  every kind whose action is a single discrete trigger with no value/
 *  duration complexity, same shape as Button. Deliberately excludes: pure
 *  display/indicator kinds (Display, StatusLed, Label, Image - nothing to
 *  fire); per-cell kinds (Transpose, Capo, WavesAutotune, ButtonGrid,
 *  Piano - binding one whole item is ambiguous, needs a cell-addressed
 *  step shape not designed yet); continuous-value kinds (Fader, XYPad,
 *  Mixer, Radial, Lfo - not a one-shot fire); and Sequencer/Macro, which
 *  already have their own dedicated ref-step kinds. Widened 2026-08-15
 *  (DEC-153's originally-flagged gap) from Button-only. */
type PaletteFireableItem = ButtonItem | PresetChangeItem | SnapshotChangeItem | StreamDeckItem;

function isPaletteFireableItem(it: Page['items'][number]): it is PaletteFireableItem {
  return it.kind === 'button' || it.kind === 'presetChange' || it.kind === 'snapshotChange' || it.kind === 'streamdeck';
}

/** Fixed section order + label for the palette's by-kind grouping
 *  (2026-08-18, operator ask: "they need to be sectioned off, with the
 *  type of item" - a flat mixed list made it hard to find a specific kind
 *  once the palette had more than a few items). Labels match the
 *  layout-canvas ADD palette's own tile names (`ItemPalette.tsx`) rather
 *  than `kindLabels.ts`'s Inspector-context names ("Line 6 Helix" reads
 *  right on a device panel, wrong as a palette section for "the guitar fx
 *  preset items"). */
const PALETTE_SECTION_ORDER: PaletteFireableItem['kind'][] = ['button', 'presetChange', 'snapshotChange', 'streamdeck'];
const PALETTE_SECTION_LABEL: Record<PaletteFireableItem['kind'], string> = {
  button: 'Button',
  presetChange: 'Guitar FX Preset',
  snapshotChange: 'Helix Snapshot',
  streamdeck: 'Stream Deck',
};

/** Data transfer mime for palette drags. */
const DRAG_MIME = 'application/x-opbuttons-timeline-drop';
interface PaletteDragPayload {
  /** Referenced layout item id - set for `buttonRef` drags. Absent for
   *  color drags. Despite the field name (kept for schema/wire stability),
   *  this resolves against ANY `PaletteFireableItem`, not only ButtonItem -
   *  see that type's doc comment for the full allowlist. */
  buttonId?: string;
  labelHint: string;
  /** What step-kind this drag produces. Used to gate compatible lanes at
   *  drop time. Defaults to `'buttonRef'` for the current palette. */
  stepKind?: 'buttonRef' | 'cue' | 'marker' | 'animation' | 'sequencer' | 'macro' | 'colorMarker' | 'targetValue';
  /** Set on `colorMarker` drags — the RGB the marker should start at. */
  color?: { r: number; g: number; b: number };
  /** Set on `targetValue` drags - the live-state target id (`LiveTarget.id`)
   *  and the value captured from it at drag-start time. */
  targetId?: string;
  value?: number | string;
}

/** Module-scoped: the step-kind of the palette drag currently in flight,
 *  or null when no palette drag is active. Set by Palette on dragstart,
 *  cleared on drop / dragend. Every lane row reads this to decide whether
 *  it's a valid drop target and dim itself when it isn't. */
const [paletteDragKind, setPaletteDragKind] =
  createSignal<NonNullable<PaletteDragPayload['stepKind']> | null>(null);

/** 16-swatch lighting palette. Curated subset of the color picker's
 *  COLOR_PRESETS matching common gel-vocabulary muscle memory. Includes
 *  black (Off) + white as the two anchors. Order: neutrals first, then
 *  warm→cool spectrum walk so the palette scans left→right like a wheel. */
/** DEC-058's sixteen stage colours. LIFTED 2026-09-17 to
 *  `lighting/palette.ts` and re-exported here under its original name, so
 *  every reference below is untouched. The Light Colour item is the second
 *  surface to offer colours and two copies of a vocabulary is exactly how
 *  `--sk-weight` ended up 500 in the app and 700 in the workbench for a whole
 *  arc - so there is now one list and both surfaces read it. */
const LIGHTING_PALETTE = STAGE_COLORS;

/** Convert an authored 0..1 RGB triple to a `#rrggbb` hex string. Used by
 *  the lighting palette's drag preview so the CSS clip renders the exact
 *  swatch colour. Handles the special-case of the "Off" swatch (pure
 *  black) — we render it as a dashed border instead of a solid fill so
 *  it's visible against the dark timeline. */
function paletteRgbToHex(rgb: { r: number; g: number; b: number }): string {
  const to = (v: number) => {
    const n = Math.max(0, Math.min(255, Math.round(v * 255)));
    return n.toString(16).padStart(2, '0');
  };
  return `#${to(rgb.r)}${to(rgb.g)}${to(rgb.b)}`;
}

/** Which lane-kinds accept which step-kinds. `mixed` accepts everything
 *  except colour (nothing samples a colour marker off a generic lane).
 *  `dmx-rig` / `dmx-fixture` (2026-08-04) are flat FX + Colour lanes —
 *  they take `animation` and `colorMarker` only, nothing else. */
function laneAcceptsStep(
  laneKind: TimelineLaneKind | undefined,
  stepKind: NonNullable<PaletteDragPayload['stepKind']>,
): boolean {
  if (!laneKind || laneKind === 'mixed') {
    return stepKind !== 'colorMarker';
  }
  if (laneKind === 'dmx-rig' || laneKind === 'dmx-fixture') {
    return stepKind === 'animation' || stepKind === 'colorMarker';
  }
  // A Guitar lane (DEC-244, made in the timeline editor v2) holds presses of
  // guitar presets and snapshots, which are `buttonRef` steps.
  if (laneKind === 'guitar') return stepKind === 'buttonRef';
  switch (stepKind) {
    case 'buttonRef':
      // Most referenceable kinds fire MIDI (Button CC/noteon, PresetChange
      // PC, SnapshotChange CC69); Stream Deck doesn't, but 'mixed' lanes
      // (the general-purpose default, handled above) already accept it
      // regardless — this branch only gates the explicitly-typed lanes.
      return laneKind === 'midi';
    case 'cue':
      // Generic cues route by the cue's action shape — for now allow all
      // signal lanes.
      return laneKind === 'midi' || laneKind === 'osc';
    case 'marker':
      return laneKind === 'marker';
    case 'sequencer':
    case 'macro':
      return laneKind === 'midi';
    case 'animation':
    case 'colorMarker':
      // FX/Colour are dmx-rig/dmx-fixture native — handled above.
      return false;
    case 'targetValue':
      // Transpose/Capo dispatch OSC, Waves key/scale dispatch MIDI CC -
      // same split as 'cue'. 'mixed' lanes (handled above) already accept
      // it regardless.
      return laneKind === 'midi' || laneKind === 'osc';
    default:
      return false;
  }
}

interface TimelineEditorProps {
  /** The app's real musical clock (App.tsx's `smoothClock`, anchored off
   *  OPBridge's `/clock/status` poll) — same signal `TimelineLauncher.tsx`'s
   *  canvas item already uses for its own live sim. Threaded down to
   *  `SelectedDetail` so the editor's preview playhead advances against
   *  REALITY instead of a second, disconnected fake clock (2026-08-18 fix —
   *  see the module doc's history; `<TimelineEditor />` used to be mounted
   *  with zero props and ran its own `requestAnimationFrame` + accumulated
   *  per-frame deltas off the timeline's own authored `bpm` field, which
   *  neither matched OPBridge's actual dispatch tempo nor self-corrected —
   *  the exact "accumulate instead of re-derive from truth" anti-pattern
   *  OPBridge itself had just fixed server-side in its own tick loop). */
  beat: Accessor<number>;
  beatsPerBar: Accessor<number>;
}

export function TimelineEditor(props: TimelineEditorProps) {
  const selectedId = timelineEditorTargetId;
  const [managerOpen, setManagerOpen] = createSignal(true);

  // Opening the editor retries any delete that could not reach its machine
  // at the time (autoSync.ts), so an offline bridge does not keep a stale copy.
  onMount(() => { void sweepPendingTimelineDeletes(); });

  const selected = createMemo<Timeline | null>(() => {
    const id = selectedId();
    if (!id) return null;
    return timelines().find((t) => t.id === id) ?? null;
  });

  // The manager lists this project's timelines (plan 3.1). One opened from a
  // Launcher that points into another project still opens: `selected` reads
  // the whole library.
  const byTier = createMemo(() => {
    const all = timelinesInActiveProject();
    const grouped: Record<TimelineTier, Timeline[]> = { action: [], song: [], genre: [] };
    for (const t of all) grouped[t.tier].push(t);
    return grouped;
  });

  function onKey(_ev: KeyboardEvent) {
    // Esc never closes the editor (operator, 2026-10-16): only its close button does.
  }

  function onCreate(tier: TimelineTier) {
    const t = createTimeline(tier, `New ${TIER_LABEL[tier].toLowerCase()} timeline`);
    selectTimelineInEditor(t.id);
  }

  function onDeleteSelected() {
    const t = selected();
    if (!t) return;
    const ok = window.confirm(`Delete timeline "${t.name}"? Undo brings it back.`);
    if (!ok) return;
    // Removed from OPBridge too (2026-09-29): before, a deleted timeline
    // stayed on every bridge it was ever pushed to, where a route or an old
    // launcher could still start it. One path for every Delete
    // (timeline/deleteTimeline.ts).
    deleteTimelineFromRig(t.id);
    selectTimelineInEditor(null);
  }

  function onDuplicateSelected() {
    const t = selected();
    if (!t) return;
    const copy = duplicateTimeline(t.id);
    if (copy) {
      selectTimelineInEditor(copy.id);
      pushToast('ok', `Duplicated "${t.name}" -> "${copy.name}"`, 2000);
    }
  }

  return (
    <Portal>
      <div
        class="timeline-editor-overlay"
        role="dialog"
        data-back-surface
        aria-modal="true"
        aria-label="Timeline editor"
        tabindex={-1}
        ref={(el) => setTimeout(() => el?.focus(), 0)}
        onKeyDown={onKey}
      >
        <div class="timeline-editor-shell" data-bar-position={barPosition()}>
          <header class="timeline-editor-head">
            <div class="timeline-editor-title">
              <Show when={!managerOpen()}>
                <button
                  type="button"
                  class="timeline-editor-manager-toggle"
                  onClick={() => setManagerOpen(true)}
                  title="Open timelines manager"
                  aria-label="Open timelines manager"
                >
                  +
                </button>
              </Show>
              {/* The Legacy timeline (DEC-240): the first editor, kept beside v2. */}
              <span class="timeline-editor-title-main">LEGACY TIMELINE</span>
              <Show when={selected()}>
                {(t) => (
                  <span class="timeline-editor-tier-badge" data-tier={t().tier}>
                    {TIER_LABEL[t().tier]}
                  </span>
                )}
              </Show>
            </div>
            <button
              type="button"
              class="timeline-editor-to-v2"
              onClick={() => switchTimelineEditor('v2')}
              title="Open this in the timeline editor"
            >
              New editor
            </button>
            <button
              type="button"
              class="timeline-editor-close"
              onClick={closeTimelineEditor}
              title="Close editor"
              aria-label="Close timeline editor"
            >
              ×
            </button>
          </header>

          <div class="timeline-editor-body" data-manager-open={managerOpen() ? 'true' : 'false'}>
            {/* LEFT — library list, grouped by tier (collapsible manager) */}
            <Show when={managerOpen()}>
              <aside class="timeline-editor-list">
                <header class="timeline-editor-list-head">
                  <span class="timeline-editor-list-head-title">MANAGER</span>
                  <button
                    type="button"
                    class="timeline-editor-list-collapse"
                    onClick={() => setManagerOpen(false)}
                    title="Collapse manager"
                    aria-label="Collapse manager"
                  >
                    ‹
                  </button>
                </header>
                <For each={TIER_ORDER.filter((tier) => CREATABLE_TIERS.has(tier) || byTier()[tier].length > 0)}>
                  {(tier) => (
                    <section class="timeline-editor-group" data-tier={tier}>
                      <header class="timeline-editor-group-head">
                        <span class="timeline-editor-group-name">{TIER_LABEL[tier]}</span>
                        <Show when={CREATABLE_TIERS.has(tier)}>
                          <button
                            type="button"
                            class="timeline-editor-group-add"
                            onClick={() => onCreate(tier)}
                            title={`Create a new ${TIER_LABEL[tier].toLowerCase()} timeline`}
                            aria-label={`Add ${TIER_LABEL[tier].toLowerCase()} timeline`}
                          >
                            +
                          </button>
                        </Show>
                      </header>
                      <p class="timeline-editor-group-hint">{TIER_HINT[tier]}</p>
                      <ul class="timeline-editor-group-items">
                        <For
                          each={byTier()[tier]}
                          fallback={<li class="timeline-editor-empty">(none)</li>}
                        >
                          {(t) => (
                            <li>
                              <button
                                type="button"
                                class="timeline-editor-list-item"
                                data-selected={selectedId() === t.id ? 'true' : 'false'}
                                onClick={() => selectTimelineInEditor(t.id)}
                              >
                                <span class="timeline-editor-list-name">{t.name}</span>
                                <span class="timeline-editor-list-meta">
                                  {t.sections.length} sect · {totalBars(t)}b
                                </span>
                              </button>
                            </li>
                          )}
                        </For>
                      </ul>
                    </section>
                  )}
                </For>
              </aside>
            </Show>

            {/* MAIN — timeline detail on top, palette below. Fills all
                remaining width so the grid spans full screen when the
                manager is collapsed. */}
            <main class="timeline-editor-main">
              <div class="timeline-editor-detail">
                <Show
                  when={selected()}
                  fallback={
                    <div class="timeline-editor-placeholder">
                      <p>Pick a timeline from the manager (left), or create one with the <b>+</b> button next to a tier.</p>
                      <p class="timeline-editor-placeholder-sub">
                        Drop items from the palette (below) onto lanes to author steps. ▶ previews on screen only; ▶| launches it for real on OPBridge.
                      </p>
                    </div>
                  }
                >
                  {(t) => (
                    <SelectedDetail
                      timeline={t()}
                      onDelete={onDeleteSelected}
                      onDuplicate={onDuplicateSelected}
                      beat={props.beat}
                      beatsPerBar={props.beatsPerBar}
                    />
                  )}
                </Show>
              </div>

              <Show when={selected()}>
                <aside class="timeline-palette">
                  <Palette />
                </aside>
              </Show>

              {/* Sections (Song/Genre) or bare length input (Action) —
                  pinned to the bottom of the main column so it stays
                  visible while palette + detail flex to fill. */}
              <Show when={selected()}>
                {(sel) => (
                  <div class="timeline-editor-sections-slot">
                    <Show
                      when={sel().tier !== 'action'}
                      fallback={<ActionLengthEditor timeline={sel()} />}
                    >
                      <SectionsEditor timeline={sel()} />
                    </Show>
                  </div>
                )}
              </Show>
            </main>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function totalBars(t: Timeline): number {
  let n = timelineLeadIn(t);
  for (const s of t.sections) n += s.lengthBars;
  return n;
}

function rgbToCss(rgb: { r: number; g: number; b: number }): string {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

/** Live-output swatch for a `dmx-rig` / `dmx-fixture` lane's gutter —
 *  samples the lane's own colour markers at the playhead (2026-08-04: no
 *  more group/sub-lane indirection, the lane carries its colour directly).
 *  Returns null when the lane has no authored colour yet — the swatch
 *  hides in that case. */
function laneSwatchColor(lane: TimelineLane, samplePosBars: number): string | null {
  const sample = sampleColorLane(lane, samplePosBars);
  return sample ? rgbToCss(colorMarker01ToU8(sample)) : null;
}

// =====================================================================
// PALETTE
// =====================================================================

function Palette() {
  const pages = pagesOfActive;

  /** On-demand drawer (confirmed 2026-08-15, not a persistent panel) -
   *  shows each live-state target's CURRENT value while authoring, so the
   *  operator can see what's live before deciding to capture it. Closed by
   *  default so it doesn't compete with the fireable-items list above. */
  const [liveStateOpen, setLiveStateOpen] = createSignal(false);

  const paletteItems = createMemo(() => {
    const out: Array<{ page: Page; items: PaletteFireableItem[] }> = [];
    for (const p of pages()) {
      const items = p.items.filter(isPaletteFireableItem);
      if (items.length > 0) out.push({ page: p, items });
    }
    return out;
  });

  /** Grouped by kind, in `PALETTE_SECTION_ORDER` regardless of authoring
   *  order, so the palette reads as stable sections (Button / Guitar FX
   *  Preset / Helix Snapshot / Stream Deck) instead of one flat mixed
   *  list. Empty kinds are dropped, not rendered as a bare header. */
  const groupedPaletteItems = createMemo(() => {
    const all = paletteItems().flatMap(({ items }) => items);
    const byKind = new Map<PaletteFireableItem['kind'], PaletteFireableItem[]>();
    for (const item of all) {
      const arr = byKind.get(item.kind);
      if (arr) arr.push(item);
      else byKind.set(item.kind, [item]);
    }
    return PALETTE_SECTION_ORDER
      .map((kind) => ({ kind, label: PALETTE_SECTION_LABEL[kind], items: byKind.get(kind) ?? [] }))
      .filter((g) => g.items.length > 0);
  });

  /** Custom drag preview: hides the browser's ghost image via a 1px
   *  transparent stand-in, then renders a floating pill (1-beat clip +
   *  info readout) portalled to <body>.
   *  - Over a lane: the clip snaps to the drop line's viewport
   *    coordinates + the info sits beside it. That way "where the pill is"
   *    equals "where the click would land."
   *  - Not over a lane: falls back to following the cursor. */
  const [dragging, setDragging] = createSignal<{
    label: string;
    kindMeta: string;
    /** Named token (`cyan` / `magenta` / …) used for CSS-side border-left
     *  tinting via `data-color`. For button drags. */
    color: string;
    /** Optional explicit background CSS color (hex/rgb). Set for color-
     *  swatch drags where the preview shows the raw swatch instead of a
     *  named-token tint. */
    bgColor?: string;
    /** Optional `--item-hue` value (2026-08-18) - the dragged item's own
     *  author-assigned accent colour (`hueOf()`/`HUE_VALUES`, DEC-127),
     *  same source + same custom-property name the palette chip and the
     *  placed step block both read, so the drag preview matches what's
     *  about to be dropped instead of a fixed cyan regardless of item. */
    hue?: string;
    cursorX: number;
    cursorY: number;
    /** Viewport coords of the drop line's top-left when over a valid lane. */
    dropX: number | null;
    dropY: number | null;
    /** Width in px of the 1-beat clip at the current zoom. */
    beatPx: number;
    laneHeight: number;
    overLane: boolean;
    rejected: boolean;
    snappedBar: number | null;
  } | null>(null);

  /** What a Button sends, minus the channel. Palette chips, drag ghosts and
   *  tooltips all read from this so a `pc` button never gets described as a
   *  CC it does not carry. */
  function buttonSends(b: ButtonItem): string {
    const action = b.actionType ?? 'cc';
    if (action === 'noteon') return `note ${b.note ?? 60}`;
    if (action === 'pc') return `PC ${b.program ?? 0}`;
    return `CC ${b.ccNumber}`;
  }

  /** Display label + secondary meta line for a palette chip, branching per
   *  kind since each `PaletteFireableItem` describes its action differently
   *  (Button: CC/note/PC + channel; PresetChange/SnapshotChange: setlist/
   *  preset/snapshot numbers; StreamDeck: the bound MCP action title). */
  function paletteItemMeta(item: PaletteFireableItem): { label: string; kindMeta: string } {
    switch (item.kind) {
      case 'button':
        // `channel` is stored 0-based; every other surface (canvas face,
        // Fader, Inspector) shows it 1-based. The palette alone printed the raw
        // value, so one button read "CH 1" on the canvas and "ch 0" here
        // (next-update-plan 1.8, 2026-09-30).
        return { label: item.name ?? buttonSends(item), kindMeta: `${buttonSends(item)} · ch ${item.channel + 1}` };
      case 'presetChange': {
        // 2026-08-18 (operator ask: "make sure to include the 01A / 03D
        // etc preset location") - was raw "Setlist N · Preset N", the one
        // spot in the codebase not using the Helix's own bank+letter
        // notation every other surface does (PresetChange.tsx's face/sub-
        // text, the Inspector, the Helix preset picker). kindMeta shows
        // this unconditionally (same "always-visible technical readout"
        // role canvas's subText plays), so the slot survives even when
        // the chip's bold `label` shows a custom name instead.
        const slot = `SL${item.setlist + 1} · ${helixSlotLabel(item.program)}`;
        return { label: item.name ?? item.presetName ?? slot, kindMeta: `Preset · ${slot}` };
      }
      case 'snapshotChange':
        return { label: item.name ?? `Snapshot ${item.snapshot}`, kindMeta: `Snapshot ${item.snapshot}` };
      case 'streamdeck':
        return {
          label: item.name ?? item.label ?? item.actionTitle ?? 'Stream Deck',
          kindMeta: item.actionTitle ?? 'Stream Deck',
        };
    }
  }

  function onDragStart(ev: DragEvent, item: PaletteFireableItem) {
    const { label, kindMeta } = paletteItemMeta(item);
    const payload: PaletteDragPayload = {
      buttonId: item.id,
      labelHint: label,
      stepKind: 'buttonRef',
    };
    const hue = hueOf(item);
    beginDrag(ev, payload, {
      label,
      kindMeta,
      color: item.color,
      ...(hue !== 'none' ? { hue: HUE_VALUES[hue] } : {}),
    });
  }

  function onColorDragStart(ev: DragEvent, swatch: (typeof LIGHTING_PALETTE)[number]) {
    const payload: PaletteDragPayload = {
      labelHint: swatch.label,
      stepKind: 'colorMarker',
      color: swatch.rgb,
    };
    const hex = paletteRgbToHex(swatch.rgb);
    beginDrag(ev, payload, {
      label: swatch.label,
      kindMeta: 'color marker',
      color: 'lighting',
      bgColor: hex,
    });
  }

  /** Capture = read the live-state target's value RIGHT NOW, at drag-start
   *  time - not at drop time (the operator may hover for a moment before
   *  releasing, and the whole point is "what was live when I grabbed it"). */
  function onLiveTargetDragStart(ev: DragEvent, target: (typeof LIVE_TARGETS)[number]) {
    const reading = target.read();
    if (!reading) return;
    const label = `${target.label}: ${reading.display}`;
    const payload: PaletteDragPayload = {
      labelHint: label,
      stepKind: 'targetValue',
      targetId: target.id,
      value: reading.value,
    };
    beginDrag(ev, payload, {
      label,
      kindMeta: 'captured value',
      color: 'amber',
    });
  }

  /** Shared drag-start scaffolding. Sets the DataTransfer payload, kills
   *  the browser's default ghost image, seeds the follower signal, and
   *  wires the window-level dragover/dragend handlers so the pill tracks
   *  the cursor + the reject-state matches what onDrop will produce. */
  function beginDrag(
    ev: DragEvent,
    payload: PaletteDragPayload,
    preview: { label: string; kindMeta: string; color: string; bgColor?: string; hue?: string },
  ) {
    if (!ev.dataTransfer) return;
    ev.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    ev.dataTransfer.effectAllowed = 'copy';

    // Broadcast the drag kind so every lane row can dim itself if it
    // won't accept this drop. Cleared in onWinDragEnd below.
    const stepKind = payload.stepKind ?? 'buttonRef';
    setPaletteDragKind(stepKind);

    // Kill the browser's default ghost image with a 1px transparent stand-in
    // so our custom follower is the only visible preview.
    const stub = document.createElement('canvas');
    stub.width = 1;
    stub.height = 1;
    ev.dataTransfer.setDragImage(stub, 0, 0);

    setDragging({
      label: preview.label,
      kindMeta: preview.kindMeta,
      color: preview.color,
      ...(preview.bgColor !== undefined ? { bgColor: preview.bgColor } : {}),
      ...(preview.hue !== undefined ? { hue: preview.hue } : {}),
      cursorX: ev.clientX,
      cursorY: ev.clientY,
      dropX: null,
      dropY: null,
      beatPx: 24,
      laneHeight: 56,
      overLane: false,
      rejected: false,
      snappedBar: null,
    });

    // Follow the cursor via window dragover. Also probe the lane under the
    // cursor to compute snappedBar + reject state so the pill's readout
    // matches exactly what onDrop would produce.
    const onWinDragOver = (e: DragEvent) => {
      const hit = findLaneAt(document.elementFromPoint(e.clientX, e.clientY));
      let overLane = false;
      let rejected = false;
      let snappedBar: number | null = null;
      let dropX: number | null = null;
      let dropY: number | null = null;
      let beatPx = 24;
      let laneHeight = 56;
      if (hit) {
        overLane = true;
        const destKind = (hit.track.dataset.kind ?? 'mixed') as TimelineLaneKind;
        rejected = !laneAcceptsStep(destKind, stepKind);
        // Compute snapped bar from the lane track's own geometry.
        const rect = hit.track.getBoundingClientRect();
        const cssVal = getComputedStyle(hit.track.parentElement ?? hit.track)
          .getPropertyValue('--px-per-bar');
        const pxPerBar = Number.parseFloat(cssVal) || 96;
        const localX = e.clientX - rect.left;
        const sigNum = 4; // matches the editor's assumed 4/4 grid
        const raw = Math.max(0, localX / pxPerBar);
        snappedBar = snapToBeat(raw, sigNum);
        // Viewport coords of the drop line + 1-beat clip footprint.
        dropX = rect.left + snappedBar * pxPerBar;
        dropY = rect.top;
        beatPx = pxPerBar / Math.max(1, sigNum);
        laneHeight = rect.height;
      }
      setDragging((prev) => prev ? {
        ...prev,
        cursorX: e.clientX,
        cursorY: e.clientY,
        dropX,
        dropY,
        beatPx,
        laneHeight,
        overLane,
        rejected,
        snappedBar,
      } : null);
    };
    const onWinDragEnd = () => {
      setDragging(null);
      setPaletteDragKind(null);
      window.removeEventListener('dragover', onWinDragOver);
      window.removeEventListener('dragend', onWinDragEnd);
      window.removeEventListener('drop', onWinDragEnd);
    };
    window.addEventListener('dragover', onWinDragOver);
    window.addEventListener('dragend', onWinDragEnd);
    window.addEventListener('drop', onWinDragEnd);
  }

  return (
    <>
      <header class="timeline-palette-head">
        <span class="timeline-palette-title">PALETTE</span>
        <span class="timeline-palette-hint">Drag onto a lane</span>
        <button
          type="button"
          class="timeline-palette-live-state-toggle"
          data-open={liveStateOpen() ? 'true' : 'false'}
          onClick={() => setLiveStateOpen((v) => !v)}
          title="Live state - see each target's current value, drag to capture it"
          aria-expanded={liveStateOpen() ? 'true' : 'false'}
        >
          LIVE STATE
        </button>
      </header>
      <Show when={liveStateOpen()}>
        <section class="timeline-palette-live-state">
          <p class="timeline-palette-live-state-hint">
            Drag a target's current value onto a lane to capture it as a step.
          </p>
          <ul class="timeline-palette-body">
            <For each={LIVE_TARGETS}>
              {(target) => {
                const reading = createMemo(() => target.read());
                return (
                  <li>
                    <div
                      class="timeline-palette-chip"
                      data-color="amber"
                      data-live-target={target.id}
                      draggable={reading() !== null}
                      onDragStart={(ev) => onLiveTargetDragStart(ev, target)}
                      title={reading()
                        ? `${target.label}: ${reading()!.display}`
                        : target.source === 'readback'
                          ? `${target.label}: the rig has not reported a value yet`
                          : `${target.label}: no value set on this device this session`}
                    >
                      <span class="timeline-palette-chip-name">{target.label}</span>
                      {/* "not set" was latch-language and became wrong for
                          three of the four targets on 2026-09-21: a readback
                          with no value has not been REPORTED, which is not the
                          same claim as you never set it. Saying the wrong one
                          sends the operator looking in the wrong place. */}
                      <span class="timeline-palette-chip-meta">
                        {reading()?.display ?? (target.source === 'readback' ? 'not reported' : 'not set')}
                      </span>
                    </div>
                  </li>
                );
              }}
            </For>
          </ul>
        </section>
      </Show>
      <Show
        when={paletteItems().length > 0}
        fallback={
          <p class="timeline-palette-empty">
            No fireable items in this project yet. Add a Button, Preset Change, Snapshot Change,
            or Stream Deck tile on the layout canvas, then drag it here.
          </p>
        }
      >
        <div class="timeline-palette-groups">
          <For each={groupedPaletteItems()}>
            {(group) => (
              <section class="timeline-palette-group">
                <h3 class="timeline-palette-group-title">
                  {group.label}
                  <span class="timeline-palette-group-count">{group.items.length}</span>
                </h3>
                <ul class="timeline-palette-body">
                  <For each={group.items}>
                    {(item) => {
                      const meta = paletteItemMeta(item);
                      return (
                        <li>
                          <div
                            class="timeline-palette-chip"
                            data-item-kind={item.kind}
                            /* Chip is a miniature of the item as it sits on the
                               canvas - CSS fixes the height and derives the width
                               from this ratio, so a wide preset tile and a square
                               Stream Deck tile stay tellable apart in the palette.
                               Guarded against a zero/absent h from an older saved
                               item, which would collapse aspect-ratio to invalid.
                               --item-hue mirrors the canvas item's own accent
                               colour exactly (same hueOf()/HUE_VALUES source
                               EditableItem.tsx uses, DEC-127) - was the raw
                               legacy `color` field before 2026-08-18, which
                               missed anything authored through the current
                               Inspector colour picker (`hue`, not `color`). */
                            style={{
                              '--chip-aspect': `${Math.max(1, item.w)} / ${Math.max(1, item.h)}`,
                              ...(hueOf(item) !== 'none' ? { '--item-hue': HUE_VALUES[hueOf(item)] } : {}),
                            }}
                            draggable={true}
                            onDragStart={(ev) => onDragStart(ev, item)}
                            title={`${meta.label} · ${meta.kindMeta}`}
                          >
                            <span class="timeline-palette-chip-name">{meta.label}</span>
                            <span class="timeline-palette-chip-meta">{meta.kindMeta}</span>
                          </div>
                        </li>
                      );
                    }}
                  </For>
                </ul>
              </section>
            )}
          </For>
        </div>
      </Show>

      {/* Lighting palette — drag any swatch onto a dmx-rig/dmx-fixture lane
          to add a colour marker. Off + White are always first as anchors. */}
      <section class="timeline-palette-lighting">
        <header class="timeline-palette-lighting-head">
          <span class="timeline-palette-lighting-title">LIGHTING</span>
          <span class="timeline-palette-lighting-hint">Drag onto colour lane</span>
        </header>
        <div class="timeline-palette-lighting-grid">
          <For each={LIGHTING_PALETTE}>
            {(swatch) => {
              const hex = paletteRgbToHex(swatch.rgb);
              const isOff = swatch.rgb.r === 0 && swatch.rgb.g === 0 && swatch.rgb.b === 0;
              return (
                <div
                  class="timeline-palette-lighting-swatch"
                  data-off={isOff ? 'true' : 'false'}
                  style={{ background: hex }}
                  draggable={true}
                  onDragStart={(ev) => onColorDragStart(ev, swatch)}
                  title={swatch.label}
                  aria-label={swatch.label}
                />
              );
            }}
          </For>
        </div>
      </section>

      {/* Custom drag preview — portalled so it escapes the palette's
          overflow clipping and floats on top of everything.
          - CLIP: when over a lane, positioned AT the drop line (exact
            snapped bar, exact 1-beat width, exact lane height) so it
            mirrors what the drop will create. Off-lane, it hides.
          - INFO: floating readout that tracks the cursor either way. */}
      <Show when={dragging()}>
        {(drag) => (
          <Portal>
            {/* Live clip preview — only when over a lane that accepts the
                drop. When the lane rejects, we suppress the placeholder
                block so only the cursor-following info readout (with its
                reject copy + the browser's ⊘ cursor) communicates state. */}
            <Show when={drag().overLane && !drag().rejected && drag().dropX !== null && drag().dropY !== null}>
              <div
                class="timeline-palette-drag-clip-live"
                data-color={drag().color}
                style={{
                  left: `${drag().dropX}px`,
                  top: `${drag().dropY! + 8}px`,
                  width: `${drag().beatPx}px`,
                  height: `${drag().laneHeight - 16}px`,
                  ...(drag().bgColor ? { background: drag().bgColor } : {}),
                  ...(drag().hue ? { '--item-hue': drag().hue } : {}),
                }}
              >
                <span class="timeline-palette-drag-clip-live-label">{drag().label}</span>
              </div>
            </Show>

            {/* Info readout — follows the cursor (offset so it doesn't
                occlude the drop line). */}
            <div
              class="timeline-palette-drag-info"
              data-reject={drag().rejected ? 'true' : 'false'}
              data-over-lane={drag().overLane ? 'true' : 'false'}
              style={{
                left: `${drag().cursorX + 14}px`,
                top: `${drag().cursorY + 14}px`,
              }}
            >
              <span class="timeline-palette-drag-info-meta">{drag().kindMeta}</span>
              <Show when={drag().overLane}>
                <Show
                  when={!drag().rejected}
                  fallback={<span class="timeline-palette-drag-info-reject">not allowed on this lane</span>}
                >
                  <span class="timeline-palette-drag-info-drop">
                    bar {((drag().snappedBar ?? 0) + 1).toFixed(2)}
                  </span>
                </Show>
              </Show>
              <Show when={!drag().overLane}>
                <span class="timeline-palette-drag-info-hint">drag onto a lane</span>
              </Show>
            </div>
          </Portal>
        )}
      </Show>
    </>
  );
}

// =====================================================================
// SELECTED DETAIL — name + grid + sections/length + preview playhead
// =====================================================================

interface SelectedDetailProps {
  timeline: Timeline;
  onDelete: () => void;
  onDuplicate: () => void;
  /** Real musical clock, threaded from `TimelineEditor` — see that
   *  component's prop doc comment for why this replaced a local fake
   *  clock. */
  beat: Accessor<number>;
  beatsPerBar: Accessor<number>;
}

function SelectedDetail(props: SelectedDetailProps) {
  const t = () => props.timeline;

  // Auto-sync to OPBridge while editing (2026-08-21) — see
  // timeline/autoSync.ts module doc for the full why. Debounced inside
  // scheduleTimelineSync; this effect just re-arms it on every content
  // change (JSON-diffed there against the last successful push, so a
  // reschedule with no real change is a cheap no-op, not a spurious push).
  //
  // MUST use `on(t, ..., { defer: true })`, not a bare `createEffect(() =>
  // scheduleTimelineSync(t()))` — confirmed by hitting this exact failure
  // live: `t()` returns the reactive Timeline store object, and
  // `JSON.stringify` inside `scheduleTimelineSync` deep-reads every nested
  // lane/step field, which a bare effect would ALSO register as its own
  // tracked dependencies (not just `t()` itself) — on a timeline with any
  // real content this produced a genuine infinite Solid update loop
  // (`RangeError: Maximum call stack size exceeded` in `runUpdates`).
  // `on()` explicitly scopes tracking to `t` alone; reads inside the
  // callback (including nested ones from `JSON.stringify`) are untracked.
  // `{ defer: true }` skips the redundant push attempt on first mount.
  createEffect(on(t, (tl) => scheduleTimelineSync(tl), { defer: true }));

  const [zoom, setZoom] = createSignal(1);
  const [previewPlaying, setPreviewPlaying] = createSignal(false);
  const [previewBar, setPreviewBar] = createSignal(0);
  const [selectedStepId, setSelectedStepId] = createSignal<string | null>(null);
  const [fixturePickerOpen, setFixturePickerOpen] = createSignal(false);
  /** Most recent lane the pointer entered. Ctrl+A uses this as "current
   *  lane" without needing DOM focus. Set on pointerdown in LaneTrack. */
  const [focusedLaneId, setFocusedLaneId] = createSignal<string | null>(null);
  /** Deferred cue: bar the playhead should JUMP to at the next whole-bar
   *  boundary. Set by clicking the ruler while playing, or by "Launch"
   *  (which cues bar 0 + arms playback to start at that boundary). Cleared
   *  once the boundary passes and the jump lands. */
  const [armedBar, setArmedBar] = createSignal<number | null>(null);
  /** When true, playback should turn ON at the next bar boundary — used by
   *  Launch to start playback aligned to a bar rather than immediately. */
  const [armedStart, setArmedStart] = createSignal(false);
  /** True while a manual "save to OPBridge" push is in flight — guards the
   *  Push button against a double-press stacking two concurrent PUTs. */
  const [pushingToBridge, setPushingToBridge] = createSignal(false);

  // ─── Real OPBridge dispatch (2026-08-16) ──────────────────────────
  // The editor's own transport is otherwise a pure visual sim (see the
  // preview-button's own "no MIDI sent" title below) — that's deliberate
  // for scrubbing/review while authoring. "Launch" (▶|) and "Stop" (■) are
  // the two controls that already promise "start/stop playback," not just
  // "move the playhead," so those are the ones wired to real dispatch —
  // same TimelineLauncher.tsx pattern (push, then fire/start on the SAME
  // tick the count-in resolves; no per-item bridge affinity here, so
  // dispatch always targets the default/primary bridge, same as an
  // unconfigured `bridgeIds`). The raw ▶ toggle and ruler-scrub stay
  // visual-only on purpose: they can resume from ANY bar, and OPBridge's
  // real primitives only support "start from bar 0" or "jump to a named
  // section," so there's no faithful real-dispatch mapping for an
  // arbitrary scrub position.
  const live = () => anyBridgeConnectedReactive(undefined);
  // ─── Beat-anchor state for the preview playhead (2026-08-18) ──────
  // `originBar`/`launchBeat` are a (bar, real-beat) pair the current
  // position is extrapolated from — `previewBar = originBar + (beat() -
  // launchBeat) / beatsPerBar`, recomputed fresh every frame from
  // `props.beat()` (never accumulated), same anchor-and-derive shape
  // `TimelineLauncher.tsx`'s own live sim already uses. `armBeatTarget` is
  // the beat value of the next bar boundary, computed once when a cue is
  // armed (Launch's count-in, or a scrub/cue while already playing) and
  // compared against on every frame until it resolves.
  //
  // 2026-08-21 — this is PURELY VISUAL now. OPBridge's own
  // `PlayheadRuntime::start()` already quantizes a Song's `/start` to the
  // next real downbeat server-side (the mechanism CP-103 fixed) — the real
  // dispatch used to wait for THIS local count-in to resolve before it was
  // even sent, stacking a second, independent quantizer on top of
  // OPBridge's, which is exactly what caused CP-103's 1-bar-late bug (the
  // two disagreed) and the count-in-never-fires bug fixed earlier this
  // session (the local wait could consume itself before the real dispatch
  // was ever queued). The real dispatch now fires immediately from
  // `launchPreview()`/`launchSection` equivalents — this local arm/count-in
  // only drives what the operator SEES, decoupled from what was actually
  // sent, matching a real-world Ableton-Sync-style "the server already
  // owns the beat" model.
  let originBar = 0;
  let launchBeat = 0;
  let armBeatTarget = 0;
  /** A Song on a connected computer follows OPBridge's run (timeline/transport.ts), read from the bridge it plays on. */
  const followsBridge = () => live() && t().tier === 'song';
  const homeTarget = createMemo(() => timelineHomeTarget(t()));
  createEffect(on(homeTarget, (h) => { const release = followTransport(h); onCleanup(release); }));
  /** The playhead is showing OPBridge's run (so its end, here or anywhere, stops it). */
  let bridgeDriven = false;
  /** 2026-08-17 — a Launch that silently did nothing (every step's pedal
   *  binding failed to resolve, so nothing was ever pushed) was a real
   *  operator-hit bug on a live Song timeline ("TESTSONG"). A console.warn
   *  nobody was looking at is not a fix for that — every dispatch outcome
   *  now surfaces a toast, not just a log line. See
   *  `bridgeSync.ts::summarizeTimelineDispatch`. */
  function dispatchRealLaunch(): void {
    if (!live()) {
      const s = notLiveToastMessage(t().name);
      pushToast(s.kind, s.message);
      return;
    }
    // The one launch every surface shares (timeline/launch.ts, 2026-10-28): each computer its part, then one press on
    // one of them. This editor kept its own copy, which pushed the whole timeline to one computer and so would have
    // overwritten that computer's part (and sounded its untied notes twice once the rig follows).
    launchTimelineOnBridge(t());
  }
  function dispatchRealStop(): void {
    if (!live() || t().tier !== 'song') return; // Action has no server-side stop (fire-and-forget)
    void stopTimelineAction(timelineHomeTarget(t())).then((r) => {
      const failed = r.filter((x) => x.kind !== 'ok');
      if (failed.length > 0) {
        pushToast('error', `"${t().name}" did not stop: ${plainFireReason(failed[0]!)}.`);
      }
    }).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error(`[timeline-editor] Stop threw unexpectedly for "${t().name}":`, err);
      pushToast('error', `"${t().name}" failed to stop: ${err instanceof Error ? err.message : String(err)}`);
    });
  }
  /** Manual "save to OPBridge" — pushes the current definition without
   *  firing/starting anything (2026-08-18 operator ask). Push otherwise
   *  only ever happens as a side effect of Launch, so an operator who just
   *  finished editing has no way to confirm the edit reached OPBridge
   *  without also triggering playback. Guarded against overlapping presses
   *  with a plain in-flight flag — a push is cheap and idempotent, but two
   *  concurrent PUTs racing on the same id is still worth avoiding. */
  function dispatchSavePush(): void {
    if (pushingToBridge()) return;
    if (!live()) {
      pushToast('warn', `"${t().name}": no bridge connected - nothing saved.`);
      return;
    }
    const timeline = t();
    const timelineName = timeline.name;
    setPushingToBridge(true);
    notePushStarted(timeline.id);
    const home = timelineHomeTarget(timeline);
    // Every part to its computer, sent even when already there (an operator pressing Save wants it saved).
    void pushTimelineParts(timeline, { force: true }).then(({ results }) => {
      const pushResults = results.length > 0 ? results : [{ target: home, kind: 'ok' } as FireResult];
      applyPartsOutcome(timeline, pushResults);
      const s = summarizeTimelinePush(timelineName, pushResults);
      pushToast(s.kind, s.message);
    }).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error(`[timeline-editor] Push threw unexpectedly for "${timelineName}":`, err);
      pushToast('error', `"${timelineName}" failed to save: ${err instanceof Error ? err.message : String(err)}`);
    }).finally(() => setPushingToBridge(false));
  }

  // Fit-to-width: the grid stretches to fill its container. Zoom is a
  // multiplier over the fit baseline. Observe the container so
  // manager-collapse (or any resize) rescales immediately.
  const [containerW, setContainerW] = createSignal(0);
  let selectedRef: HTMLDivElement | undefined;
  createEffect(() => {
    if (!selectedRef) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerW(e.contentRect.width);
    });
    ro.observe(selectedRef);
    // Seed synchronously + on next frame — first render often has
    // clientWidth = 0 before the initial layout pass settles.
    setContainerW(selectedRef.clientWidth);
    requestAnimationFrame(() => {
      if (selectedRef) setContainerW(selectedRef.clientWidth);
    });
    onCleanup(() => ro.disconnect());
  });

  // Any press inside the editor gives keyboard focus to this wrapper, which
  // is where the shortcuts live (`onGridKey`: Ctrl+C/X/V/D/Z/A, Delete).
  // Found 2026-10-01 by the paste/duplicate smoke: a step block's pointerdown
  // calls preventDefault (no text selection while dragging), which ALSO stops
  // the browser moving focus - so on a freshly opened editor, clicking a block
  // left focus on the overlay and every shortcut silently did nothing (and
  // Ctrl+D fell through to the browser's "bookmark this page"). The old smokes
  // focused the wrapper from code, which hid it. Capture phase, because blocks
  // stop propagation; text fields keep their own focus.
  onMount(() => {
    const el = selectedRef;
    if (!el) return;
    const grab = (ev: PointerEvent) => {
      const t = ev.target as HTMLElement | null;
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      // Also from a focused field inside (the name box): a press on a block
      // is a statement that the next keys are for the timeline.
      if (document.activeElement !== el) el.focus({ preventScroll: true });
    };
    el.addEventListener('pointerdown', grab, { capture: true });
    onCleanup(() => el.removeEventListener('pointerdown', grab, { capture: true }));
  });

  const showSections = () => t().tier !== 'action';
  const totalB = () => Math.max(1, totalBars(t()));
  const pxPerBar = () => {
    // Grid = gutter + bars * pxPerBar. Solve for pxPerBar so the grid
    // exactly fills the container at zoom = 1; zoom stretches beyond,
    // adding a horizontal scrollbar.
    const w = containerW();
    if (w <= 0) return PX_PER_BAR_BASE * zoom();
    // Account for the gutter + the grid's own 1px borders. No scrollbar
    // buffer — the grid only overflows when zoom > 1, and we don't want
    // to reserve dead space at zoom 1.
    const usable = Math.max(0, w - LANE_GUTTER_W - 2);
    // Fit so exactly totalBars fills the container at zoom = 1.
    const fit = usable / totalB();
    return Math.max(24, fit * zoom());
  };

  /** Beat value of the NEXT whole-bar boundary from `atBeat` — the anchor
   *  every cue (Launch's count-in, or a scrub/cue while already playing)
   *  resolves against. Computed once when arming, not re-derived per
   *  frame, so it stays a fixed target even as `props.beat()` keeps
   *  moving. */
  function nextBoundaryBeat(atBeat: number): number {
    const bpBar = Math.max(1, props.beatsPerBar());
    return (Math.floor(atBeat / bpBar) + 1) * bpBar;
  }

  // Preview playhead — derives `previewBar` fresh every frame from the REAL
  // musical clock (`props.beat()`, App.tsx's smoothClock, itself anchored
  // off OPBridge's `/clock/status`), never accumulated. `props.beat()`
  // already updates at 60Hz via its own rAF loop (see smoothClock.ts), so
  // reading it here is enough to get smooth motion without a second local
  // rAF loop. Also resolves the armed cue (`armedBar`/`armedStart`) exactly
  // at `armBeatTarget` — Launch's count-in flips play on; a plain
  // scrub/cue-while-playing just jumps.
  createEffect(() => {
    const b = props.beat();
    // A Song on a connected computer: the playhead is where OPBridge's run is (2026-10-16, the transport plan: this
    // editor armed on the next bar line of the app's clock, the count-in that put the v2 editor a bar behind a
    // followed song). Only while a run of THIS timeline is there, so the visual-only preview toggle still works.
    if (followsBridge()) {
      const home = homeTarget();
      const run = transportRun(home);
      const at = run && run.timelineId === t().id ? transportBar(home, t()) : null;
      if (at) {
        bridgeDriven = true;
        if (at.state === 'queued') { setArmedStart(true); setPreviewPlaying(false); setPreviewBar(at.pos); }
        else if (at.state === 'paused') { setArmedStart(false); setPreviewPlaying(false); setPreviewBar(at.pos); }
        else { setArmedStart(false); setPreviewPlaying(!at.ended); setPreviewBar(at.ended ? totalB() : at.pos); }
        setArmedBar(null);
        return;
      }
      if (bridgeDriven) { bridgeDriven = false; setPreviewPlaying(false); setArmedStart(false); setArmedBar(null); return; }
    }
    const cue = armedBar();
    const startArmed = armedStart();
    if (!previewPlaying() && !startArmed) return; // fully idle

    if ((cue !== null || startArmed) && b >= armBeatTarget) {
      // Boundary reached — resolve the cue, re-anchor from here, and (for
      // Launch specifically) flip play on. Fire the real dispatch on the
      // SAME tick the cue resolves, so real MIDI and the visual playhead
      // start together.
      const landBar = cue ?? 0;
      setPreviewBar(landBar);
      originBar = landBar;
      launchBeat = b;
      setArmedBar(null);
      if (startArmed) { setArmedStart(false); setPreviewPlaying(true); }
      // Real dispatch already went out immediately when the arm was set —
      // see the module-level note above `armBeatTarget`. Nothing to fire
      // here anymore; this block is purely the visual resolution.
      return;
    }

    // Launch's own count-in (not yet actually playing) holds the playhead
    // static — it must not appear to move before real playback (and real
    // MIDI) actually starts at the boundary above. 2026-08-20 correction:
    // this effect used to advance `previewBar` during the wait too ("play
    // the current bar out visually"), which read as the timeline already
    // playing. A scrub/re-Launch cue armed WHILE already playing must still
    // advance normally below (tempo keeps running until its own boundary).
    if (!previewPlaying()) return;

    // Normal advance — only reached once actually playing.
    const bpBar = Math.max(1, props.beatsPerBar());
    const tot = totalB();
    // Through each section's end rule, as the v2 editor and the Launcher (timeline/playhead.ts, DEC-227).
    const at = playheadAt(t(), originBar, (b - launchBeat) / bpBar, timelineLoops(t()));
    const pos = at.pos;
    if (at.ended) {
      setPreviewBar(tot);
      setPreviewPlaying(false);
      setArmedBar(null);
      setArmedStart(false);
      return;
    }
    setPreviewBar(pos);
  });

  function togglePreview() {
    const turningOn = !previewPlaying();
    if (turningOn) {
      // Anchor from wherever the playhead currently sits — no count-in on
      // the raw toggle, it starts immediately (this control's own title
      // promises "visual only, no MIDI sent").
      originBar = previewBar();
      launchBeat = props.beat();
    }
    setPreviewPlaying(turningOn);
    // Manually toggling play cancels any pending Launch. Deliberately no
    // real dispatch here — it can resume from any scrubbed-to bar, which
    // has no faithful mapping onto OPBridge's real start-from-0/
    // jump-to-section primitives.
    setArmedStart(false);
  }
  function stopPreview() {
    setPreviewPlaying(false);
    setPreviewBar(0);
    setArmedBar(null);
    setArmedStart(false);
    dispatchRealStop();
    if (followsBridge()) noteTransportStop(homeTarget());
  }
  /** Scrub from the ruler. Snap to bar. When stopped, jump immediately;
   *  when playing, arm the cue and let the beat-driven effect jump at the
   *  next real bar boundary (tempo keeps running until then). */
  function scrubToBar(barIx: number) {
    const clamped = Math.max(0, Math.min(totalB() - 1, Math.round(barIx)));
    if (previewPlaying()) {
      armBeatTarget = nextBoundaryBeat(props.beat());
      setArmedBar(clamped);
    } else {
      setPreviewBar(clamped);
      setArmedBar(null);
    }
  }
  /** Scrub from empty space in a lane track. Beat-snap (finer than the
   *  ruler's bar-snap). Same playing/stopped semantics as scrubToBar. */
  function scrubToPosition(positionBars: number) {
    const sigNum = t().signature?.num ?? 4;
    const snapped = Math.max(0, Math.min(totalB(), snapToBeat(positionBars, sigNum)));
    if (previewPlaying()) {
      // Reuse the ruler cue channel — we still jump at the next whole-bar
      // boundary so tempo doesn't glitch.
      armBeatTarget = nextBoundaryBeat(props.beat());
      setArmedBar(snapped);
    } else {
      setPreviewBar(snapped);
      setArmedBar(null);
    }
  }
  /** Launch: start playback at the next real bar boundary. Cues bar 0 and,
   *  if not already playing, arms a count-in — the playhead stays static
   *  (see `data-armed` on the Launch button for the "waiting" indicator)
   *  until the boundary actually resolves, when real playback (and real
   *  MIDI) begin together. The beat-driven effect above treats "playing OR
   *  armedStart" as reason enough to keep watching for that boundary. */
  function launchPreview() {
    const b = props.beat();
    // 2026-08-21 — dispatch fires IMMEDIATELY, not gated on the local
    // count-in resolving. OPBridge's own `/start` already quantizes a Song
    // to the next real downbeat server-side, so waiting for a SECOND,
    // independent local quantizer before ever sending the command was
    // redundant — and the two disagreeing is exactly what caused CP-103's
    // 1-bar-late bug and (via a Solid reentrant-effect ordering defect)
    // the count-in-consumes-itself bug fixed earlier this session. The
    // local arm below is now purely the visual "waiting to launch"
    // indicator; it no longer gates anything real.
    dispatchRealLaunch();
    // On a connected computer the run's own state drives the playhead from here (shown waiting at once).
    if (followsBridge()) { noteTransportLaunch(homeTarget(), t().id, 0); return; }
    armBeatTarget = nextBoundaryBeat(b);
    if (!previewPlaying()) {
      originBar = previewBar();
      launchBeat = b;
      setArmedStart(true);
    }
    // Already playing — Launch acts as "cue back to bar 0 at next bar" for
    // the VISUAL playhead (the real re-dispatch above already happened;
    // OPBridge's own "(re)start... from bar 0" is idempotent-safe to call
    // again while running).
    setArmedBar(0);
  }
  function cancelArmed() {
    setArmedBar(null);
    setArmedStart(false);
    // The real dispatch already went out when the arm was set (see
    // `launchPreview` above) — OPBridge may already be counting down to
    // its own next-bar start. Clearing only the local visual would leave a
    // "cancelled" Song silently starting anyway; `dispatchRealStop` sends
    // a real stop for Song tier to actually abort it. Action tier has no
    // per-instance stop primitive server-side (fire-and-forget, same
    // documented limitation `stopPreview`'s own comment already carries),
    // so an armed Action fire genuinely cannot be recalled once armed.
    dispatchRealStop();
  }

  /** Currently-selected keyframes as {laneId, stepId} pairs. Reads from
   *  the shared selection signal; falls back to `selectedStepId` (single
   *  step-block selection) when the multi-select signal is empty. */
  function selectedPairs(): Array<{ laneId: string; stepId: string }> {
    const set = selection();
    if (set.size > 0) {
      // Only steps that still exist: an undo, a delete or a synced change can
      // remove a selected step without clearing its key (2026-10-01).
      const live = [...set].map((k) => parseSelKey(k)).filter(({ laneId, stepId }) =>
        t().lanes.some((l) => l.id === laneId && l.steps.some((s) => s.id === stepId)));
      if (live.length > 0) return live;
    }
    const sid = selectedStepId();
    if (!sid) return [];
    for (const lane of t().lanes) {
      if (lane.steps.some((s) => s.id === sid)) {
        return [{ laneId: lane.id, stepId: sid }];
      }
    }
    return [];
  }

  /** All colour-marker keyframes in a lane. Used by Ctrl+A on a focused
   *  lane. */
  function keyframeStepsOnLane(laneId: string): string[] {
    const lane = t().lanes.find((l) => l.id === laneId);
    if (!lane) return [];
    return lane.steps
      .filter((s) => s.kind === 'colorMarker')
      .map((s) => s.id);
  }

  function deleteSelection(): void {
    const pairs = selectedPairs();
    if (pairs.length === 0) return;
    removeSteps(t().id, pairs);
    clearSelection();
    setSelectedStepId(null);
  }

  async function copySelection(cut: boolean): Promise<void> {
    const pairs = selectedPairs();
    if (pairs.length === 0) return;
    const timeline = t();
    // Two-pass: first find the leftmost selected step so we can express
    // every other step's offset relative to it. On paste the leftmost
    // lands at the playhead and everything else preserves its spacing.
    // Playhead position at copy time is intentionally NOT used — user
    // preference 2026-07-10 (predictable "leftmost = playhead" mental
    // model beats "same relation to playhead as copy time").
    interface Draft { step: TimelineStep; lane: typeof timeline.lanes[number] }
    const drafts: Draft[] = [];
    let leftmost = Infinity;
    let rightmost = -Infinity;
    for (const { laneId, stepId } of pairs) {
      const lane = timeline.lanes.find((l) => l.id === laneId);
      if (!lane) continue;
      const step = lane.steps.find((s) => s.id === stepId);
      if (!step) continue;
      if (step.position < leftmost) leftmost = step.position;
      if (step.position > rightmost) rightmost = step.position;
      drafts.push({ step, lane });
    }
    if (drafts.length === 0 || !isFinite(leftmost)) return;

    const entries: ClipboardEntry[] = drafts.map(({ step, lane }) => {
      const stepCopy: Record<string, unknown> = { ...step };
      delete stepCopy.id;
      // position is redundant with offsetFromLeftmostBars now; drop it to
      // keep the payload lean.
      delete stepCopy.position;
      const entry: ClipboardEntry = {
        offsetFromLeftmostBars: step.position - leftmost,
        sourceLaneId: lane.id,
        sourceLaneKind: lane.laneKind,
        step: stepCopy,
      };
      return entry;
    });

    const span = rightmost - leftmost;
    const payload: ClipboardPayload = {
      version: CLIPBOARD_VERSION,
      spanBars: span,
      entries,
    };
    await writeClipboard(payload);
    if (cut) deleteSelection();
  }

  async function pasteAtPlayhead(): Promise<void> {
    const payload = await readClipboard();
    if (!payload || payload.entries.length === 0) return;
    await pastePayloadAtPlayhead(payload);
  }

  /** Right-click-empty-lane → Paste. Anchors at the click bar (not the
   *  playhead) and routes to `laneId` (not source-lane-first). Each
   *  entry's `offsetFromLeftmostBars` becomes its offset from the click
   *  position, so the multi-selection's spatial shape is preserved. */
  async function pasteAtPosition(laneId: string, bar: number): Promise<void> {
    const payload = await readClipboard();
    if (!payload || payload.entries.length === 0) return;
    await pastePayloadAtPlayhead(payload, {
      playheadOverride: bar,
      preferLaneId: laneId,
    });
  }

  /** Core paste implementation shared by clipboard-paste + Duplicate.
   *  Handles playhead-relative anchoring, lane routing (source-lane-first
   *  → first-compatible-lane fallback), and the negative-bar confirmation
   *  prompt.
   *
   *  `paletteOverride` shifts the playhead reference for Duplicate so the
   *  clones land offset from the source by `spanBars + 1 beat` — cf.
   *  Ableton/Figma duplicate-in-place behaviour. */
  async function pastePayloadAtPlayhead(
    payload: ClipboardPayload,
    opts: {
      playheadOverride?: number;
      keepSource?: boolean;
      /** When set (right-click-empty-lane → Paste), route every entry to
       *  this lane if it accepts the step kind — beats source-lane-first.
       *  User explicitly picked this lane by right-clicking it. */
      preferLaneId?: string;
    } = {},
  ): Promise<void> {
    const timeline = t();
    const playhead = opts.playheadOverride ?? previewBar();

    // Build source-lane lookup first — preferred routing target when the
    // source lane still exists in the destination timeline (per user's
    // "same lane, same fixture" preference).
    const laneById = new Map<string, typeof timeline.lanes[number]>();
    for (const l of timeline.lanes) laneById.set(l.id, l);

    // First pass: resolve each entry to (destLaneId, stepPos). Collect any
    // entries that would land before bar 0 so we can prompt the user.
    interface Resolved { destLaneId: string; stepPos: number; entry: ClipboardEntry }
    const resolved: Resolved[] = [];
    const negatives: Resolved[] = [];
    let unroutable = 0;

    const preferLane = opts.preferLaneId ? laneById.get(opts.preferLaneId) : undefined;

    for (const e of payload.entries) {
      const stepKind = (e.step.kind as string | undefined) ?? 'cue';
      let destLaneId: string | undefined;

      // Route order:
      //   0. preferLaneId (right-click-empty-lane → Paste) — beats source-lane
      //      when the operator explicitly picked a target by right-clicking.
      //   1. Source lane by id — same lane in same timeline (or same-id
      //      after cross-timeline paste if by chance the lane exists).
      //   2. Fallback: first lane that laneAcceptsStep(...) accepts.
      if (preferLane && laneAcceptsStep(preferLane.laneKind, stepKind as never)) {
        destLaneId = preferLane.id;
      }
      if (!destLaneId && e.sourceLaneId && laneById.has(e.sourceLaneId)) {
        const srcLane = laneById.get(e.sourceLaneId)!;
        if (laneAcceptsStep(srcLane.laneKind, stepKind as never)) {
          destLaneId = srcLane.id;
        }
      }
      if (!destLaneId) {
        for (const l of timeline.lanes) {
          if (laneAcceptsStep(l.laneKind, stepKind as never)) {
            destLaneId = l.id;
            break;
          }
        }
      }
      if (!destLaneId) { unroutable += 1; continue; }

      // Beat-snap the resulting position so pasted blocks land on the
      // grid — playhead + offset can be fractional if the paste anchor
      // was mid-beat (right-click-empty-lane paste uses the raw click x).
      // Snap once here rather than per-anchor to keep the math simple.
      const sigNum = timeline.signature?.num ?? 4;
      const stepPos = snapToBeat(playhead + e.offsetFromLeftmostBars, sigNum);
      const item: Resolved = { destLaneId, stepPos, entry: e };
      if (stepPos < 0) negatives.push(item);
      else resolved.push(item);
    }

    if (resolved.length === 0 && negatives.length === 0) return;

    // Negative-bar case: prompt user to accept (skip the offenders) or
    // cancel the whole paste. Per user preference 2026-07-10.
    if (negatives.length > 0) {
      const total = resolved.length + negatives.length;
      const ok = window.confirm(
        `${negatives.length} of ${total} block${total === 1 ? '' : 's'} would land before bar 0.\n\n` +
        `Accept - paste the ${resolved.length} in-range block${resolved.length === 1 ? '' : 's'} and skip the others.\n` +
        `Cancel - don't paste anything.`,
      );
      if (!ok) return;
    }

    if (resolved.length === 0) return;

    const additions: Array<{ laneId: string; step: TimelineStep }> = [];
    const newSelection: string[] = [];

    for (const r of resolved) {
      const seed = { ...r.entry.step };
      seed.id = ulid();
      seed.position = r.stepPos;
      const newStep = seed as unknown as TimelineStep;
      additions.push({ laneId: r.destLaneId, step: newStep });
      newSelection.push(selKey(r.destLaneId, newStep.id));
    }

    // Paste and Duplicate cut what they land on, the same DAW rule as a drag
    // (operator 2026-10-01: "same as a drag"). Each pasted block trims, splits
    // or removes the part of an EXISTING block it covers; pasted blocks never
    // cut each other. Replaced a window that deleted steps STARTING between
    // the first and last pasted start, ignoring lengths - so a block starting
    // before the paste, or under a pasted block's tail, stayed stacked - and
    // Duplicate skipped even that. Duplicate's copies land after the
    // selection's END (`duplicateSelection`), so they never cut their source.
    void opts.keepSource;
    placeStepsBulk(timeline.id, additions);
    setSelection(newSelection);
    setSelectedStepId(null);
    void unroutable; // future: surface a toast when non-zero
  }

  /** Duplicate the current selection in place — clone every step, offset
   *  by (selection span + 1 beat) so the clones land cleanly after the
   *  originals. Does NOT touch the system clipboard — matches Figma/Ableton
   *  behaviour where Ctrl+D and Ctrl+C are separate concerns. Selection
   *  moves to the new clones so the user can immediately drag them if they
   *  want to reposition. */
  async function duplicateSelection(): Promise<void> {
    const pairs = selectedPairs();
    if (pairs.length === 0) return;
    const timeline = t();

    interface Draft { step: TimelineStep; lane: typeof timeline.lanes[number] }
    const drafts: Draft[] = [];
    const sigNum = timeline.signature?.num ?? 4;
    let leftmost = Infinity;
    /** Where the selection's last block ENDS (2026-10-01). It was the last
     *  START plus one beat, right only for one-beat blocks: a one-bar block
     *  duplicated landed a beat into itself, which the paste's cut rule would
     *  now trim. */
    let selectionEnd = -Infinity;
    for (const { laneId, stepId } of pairs) {
      const lane = timeline.lanes.find((l) => l.id === laneId);
      if (!lane) continue;
      const step = lane.steps.find((s) => s.id === stepId);
      if (!step) continue;
      if (step.position < leftmost) leftmost = step.position;
      selectionEnd = Math.max(selectionEnd, stepEndBars(step, sigNum));
      drafts.push({ step, lane });
    }
    if (drafts.length === 0 || !isFinite(leftmost)) return;

    const entries: ClipboardEntry[] = drafts.map(({ step, lane }) => {
      const stepCopy: Record<string, unknown> = { ...step };
      delete stepCopy.id;
      delete stepCopy.position;
      const entry: ClipboardEntry = {
        offsetFromLeftmostBars: step.position - leftmost,
        sourceLaneId: lane.id,
        sourceLaneKind: lane.laneKind,
        step: stepCopy,
      };
      return entry;
    });

    const span = selectionEnd - leftmost;
    // Clones start where the selection ends, so the copy follows it with no
    // gap and never covers it. For one-beat blocks this is the old
    // "last start + 1 beat".
    const cloneAnchor = selectionEnd;
    const payload: ClipboardPayload = {
      version: CLIPBOARD_VERSION,
      spanBars: span,
      entries,
    };
    await pastePayloadAtPlayhead(payload, {
      playheadOverride: cloneAnchor,
      keepSource: true,
    });
  }

  function selectAllOnFocusedLane(): void {
    // Focused lane = the lane row containing the currently-active
    // pointer. We track this via a most-recently-focused-lane signal
    // updated by pointerdown in LaneTrack. Falls back to the lane of
    // the singly-selected step if present.
    let laneId = focusedLaneId();
    if (!laneId) {
      const first = selectedPairs()[0];
      if (first) laneId = first.laneId;
    }
    if (!laneId) return;
    const stepIds = keyframeStepsOnLane(laneId);
    if (stepIds.length === 0) return;
    setSelection(stepIds.map((sid) => selKey(laneId!, sid)));
  }

  function onGridKey(ev: KeyboardEvent) {
    // Ignore keys that originated in a text input — otherwise Space
    // triggers preview and Backspace deletes the selected step while the
    // user is trying to edit a name / number.
    const tgt = ev.target as HTMLElement | null;
    if (tgt) {
      const tag = tgt.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt.isContentEditable) return;
    }
    const mod = ev.ctrlKey || ev.metaKey;
    if (mod && ev.key.toLowerCase() === 'z') {
      ev.preventDefault();
      // stopPropagation so the global undo router (installed in App.tsx)
      // doesn't also fire — timeline has its own scoped undo, this
      // handler wins when the grid has focus.
      ev.stopPropagation();
      if (ev.shiftKey) announceHistoryResult('redo', redo(), 'timeline');
      else announceHistoryResult('undo', undo(), 'timeline');
      return;
    }
    if (mod && ev.key.toLowerCase() === 'y') {
      ev.preventDefault();
      ev.stopPropagation();
      announceHistoryResult('redo', redo(), 'timeline');
      return;
    }
    if (mod && ev.key.toLowerCase() === 'c') {
      ev.preventDefault();
      copySelection(false);
      return;
    }
    if (mod && ev.key.toLowerCase() === 'x') {
      ev.preventDefault();
      copySelection(true);
      return;
    }
    if (mod && ev.key.toLowerCase() === 'v') {
      ev.preventDefault();
      pasteAtPlayhead();
      return;
    }
    if (mod && ev.key.toLowerCase() === 'd') {
      ev.preventDefault();
      duplicateSelection();
      return;
    }
    if (mod && ev.key.toLowerCase() === 'a') {
      ev.preventDefault();
      selectAllOnFocusedLane();
      return;
    }
    if (ev.key === 'Escape') {
      // Innermost first (2026-09-30): an Esc that clears a selection stops
      // here. It used to bubble on to the editor's own Esc and close the
      // whole Timelines editor as well. Only an Esc with nothing selected
      // goes on to close it.
      const had = selection().size > 0 || selectedStepId() !== null;
      clearSelection();
      setSelectedStepId(null);
      if (had) ev.stopPropagation();
      return;
    }
    if (ev.key === ' ') {
      ev.preventDefault();
      togglePreview();
      return;
    }
    if (ev.key === 'Delete' || ev.key === 'Backspace') {
      deleteSelection();
      return;
    }
  }


  return (
    <div class="timeline-editor-selected" tabindex={0} onKeyDown={onGridKey} ref={selectedRef}>
      {/* Name + preview transport + delete */}
      <div class="timeline-editor-name-row">
        <input
          class="timeline-editor-name-input"
          type="text"
          value={t().name}
          onFocus={(ev) => {
            (ev.currentTarget as HTMLInputElement).dataset.focusValue = t().name;
          }}
          onInput={(ev) => preserveCaret(ev, (v) => renameTimeline(t().id, v))}
          onBlur={(ev) => {
            const prev = (ev.currentTarget as HTMLInputElement).dataset.focusValue ?? '';
            commitTimelineName(t().id, prev);
          }}
          onKeyDown={(ev) => {
            const input = ev.currentTarget as HTMLInputElement;
            if (ev.key === 'Enter') {
              input.blur();
            } else if (ev.key === 'Escape') {
              ev.stopPropagation();
              const prev = input.dataset.focusValue ?? '';
              renameTimeline(t().id, prev);
              input.blur();
            }
          }}
          spellcheck={false}
          aria-label="Timeline name"
        />
        <TimelineSyncPill id={t().id} />
        <TimelinePlaysOnPill timeline={t()} />
        <div class="timeline-editor-preview-controls">
          <button
            type="button"
            class="timeline-editor-preview-btn"
            data-playing={previewPlaying() ? 'true' : 'false'}
            onClick={togglePreview}
            title="Preview playhead (space) - visual only, no MIDI sent"
          >
            {previewPlaying() ? '❚❚' : '▶'}
          </button>
          <button
            type="button"
            class="timeline-editor-preview-btn"
            data-armed={armedStart() ? 'true' : 'false'}
            data-live={live() ? 'true' : 'false'}
            onClick={armedStart() ? cancelArmed : launchPreview}
            title={
              armedStart()
                ? 'Cancel launch'
                : previewPlaying()
                  ? `Cue back to bar 1 at next bar${live() ? ' - fires real MIDI via OPBridge' : ' (no bridge connected, visual only)'}`
                  : `Launch - start playback at next bar boundary${live() ? ' - fires real MIDI via OPBridge' : ' (no bridge connected, visual only)'}`
            }
          >
            ▶|
          </button>
          <button
            type="button"
            class="timeline-editor-preview-btn"
            onClick={stopPreview}
            title={`Stop and rewind${live() && t().tier === 'song' ? ' - also stops real playback on OPBridge' : ''}`}
          >
            ■
          </button>
          <button
            type="button"
            class="timeline-editor-preview-btn timeline-editor-loop-btn"
            data-loop={timelineLoops(t()) ? 'true' : 'false'}
            onClick={() => setTimelineLoop(t().id, !timelineLoops(t()))}
            title={
              timelineLoops(t())
                ? 'Loop on - the timeline repeats at the end (launchers set to Inherit follow this)'
                : 'Loop off - the timeline plays through and stops'
            }
            aria-label="Toggle timeline loop"
            aria-pressed={timelineLoops(t()) ? 'true' : 'false'}
          >
            ⟳
          </button>
          <button
            type="button"
            class="timeline-editor-preview-btn timeline-editor-push-btn"
            data-live={live() ? 'true' : 'false'}
            data-busy={pushingToBridge() ? 'true' : 'false'}
            onClick={dispatchSavePush}
            title={
              live()
                ? 'Save this timeline\'s current definition to OPBridge - does not play anything'
                : 'Save to OPBridge (no bridge connected)'
            }
            aria-label="Save timeline to OPBridge"
          >
            ↑
          </button>
          <label class="timeline-editor-zoom">
            Zoom
            <input
              type="range"
              min={1}
              max={4}
              step={0.1}
              value={zoom()}
              onInput={(ev) => setZoom(Number((ev.currentTarget as HTMLInputElement).value))}
            />
          </label>
        </div>
        <button
          type="button"
          class="timeline-editor-duplicate"
          onClick={props.onDuplicate}
          title="Duplicate this timeline"
        >
          Duplicate
        </button>
        <button
          type="button"
          class="timeline-editor-delete"
          onClick={props.onDelete}
          title="Delete this timeline"
        >
          Delete
        </button>
      </div>

      {/* DAW GRID */}
      <TimelineGrid
        timeline={t()}
        pxPerBar={pxPerBar()}
        showSections={showSections()}
        previewBar={previewBar()}
        armedBar={armedBar()}
        onScrubToBar={scrubToBar}
        onScrubToPosition={scrubToPosition}
        onFocusLane={setFocusedLaneId}
        onCopy={() => copySelection(false)}
        onCut={() => copySelection(true)}
        onPaste={() => pasteAtPlayhead()}
        onDuplicate={() => duplicateSelection()}
        onPasteAtPosition={(laneId, bar) => pasteAtPosition(laneId, bar)}
        selectedStepId={selectedStepId()}
        onSelectStep={setSelectedStepId}
        onOpenFixturePicker={() => setFixturePickerOpen(true)}
        onAddLane={(kind) => addLane(t().id, kind)}
        fitMode={zoom() <= 1.001}
        zoom={zoom()}
        setZoom={setZoom}
      />

      <Show when={fixturePickerOpen()}>
        <FixturePicker
          onPick={(fixtureId) => {
            addDmxFixtureLane(t().id, fixtureId);
            setFixturePickerOpen(false);
          }}
          onCancel={() => setFixturePickerOpen(false)}
        />
      </Show>

      {/* Legend — sections editor is hoisted OUT of here to sit at the
          bottom of the main column (below palette). See TimelineEditor
          layout: palette grows to fill; sections pinned to bottom. */}
      <p class="timeline-editor-footnote">
        Drag an item from the palette (below) onto a lane row to place it. Right-click a placed step for its menu. Sections show as colored bands. ▶ previews on screen only and sends nothing; ▶| launches it on OPBridge.
      </p>
    </div>
  );
}

// =====================================================================
// GRID — ruler + section bands + lanes + steps + playhead
// =====================================================================

interface TimelineGridProps {
  timeline: Timeline;
  pxPerBar: number;
  showSections: boolean;
  previewBar: number;
  /** Bar the playhead will JUMP to at the next whole-bar boundary. Null =
   *  no cue armed. Renders as a flashing indicator on the ruler at that
   *  bar. */
  armedBar: number | null;
  /** Click-in-ruler callback. Receives a bar-snapped position. */
  onScrubToBar: (barIx: number) => void;
  /** Click-in-empty-lane-space callback. Receives a beat-snapped
   *  fractional bar position. */
  onScrubToPosition: (positionBars: number) => void;
  /** Called by LaneTrack on pointerdown so parent knows which lane the
   *  pointer is currently on — used for Ctrl+A "select all on lane." */
  onFocusLane: (laneId: string | null) => void;
  /** Clipboard operations, passed straight through to LaneTrack menu. */
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
  onPasteAtPosition: (laneId: string, bar: number) => void;
  selectedStepId: string | null;
  onSelectStep: (id: string | null) => void;
  onOpenFixturePicker: () => void;
  onAddLane: (kind: TimelineLaneKind) => void;
  /** True when zoom = 1 (fit). Disables horizontal scroll on the grid so
   *  sub-pixel overshoot doesn't produce a scrollbar at fit. */
  fitMode: boolean;
  zoom: number;
  setZoom: (v: number | ((prev: number) => number)) => void;
}

/** Wheel bindings, matching Ableton/DAW convention:
 *   Alt   + wheel up   = zoom in    (down = zoom out)
 *   Shift + wheel up   = scroll left (down = scroll right)
 *  Plain wheel = default (vertical page scroll). */
const ZOOM_STEP = 0.35;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const SHIFT_SCROLL_PX = 80;

function TimelineGrid(props: TimelineGridProps) {
  const t = () => props.timeline;
  const totalB = () => Math.max(1, totalBars(t()));
  /** Step id -> why it won't play where this timeline plays (plan 3.3).
   *  The same compile a push makes, so the mark and the warning agree. */
  const stepWarnings = createMemo(() => timelineStepWarnings(t()));
  const gridPxWidth = () => totalB() * props.pxPerBar;

  /** Add-lane menu — opens off the `+` in the LANES corner, OR off the
   *  empty-timeline prompt's own "+ Add a lane" button when the timeline
   *  has zero lanes (2026-08-04). Portalled to <body> so it escapes the
   *  grid's overflow clipping. Position tracks whichever trigger opened
   *  it. Closes on outside click / Escape / selection. */
  const [addMenuOpen, setAddMenuOpen] = createSignal(false);
  const [addMenuPos, setAddMenuPos] = createSignal<{ top: number; right: number } | null>(null);
  let addTriggerRef: HTMLButtonElement | undefined;
  let emptyAddTriggerRef: HTMLButtonElement | undefined;
  let activeTriggerEl: HTMLElement | undefined;

  function closeAddMenu() { setAddMenuOpen(false); }
  function openAddMenuFrom(el: HTMLElement | undefined) {
    if (!el) return;
    activeTriggerEl = el;
    const rect = el.getBoundingClientRect();
    setAddMenuPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
    setAddMenuOpen(true);
  }
  function openAddMenu() { openAddMenuFrom(addTriggerRef); }
  function pickLane(kind: TimelineLaneKind) {
    props.onAddLane(kind);
    closeAddMenu();
  }
  function pickFixture() {
    props.onOpenFixturePicker();
    closeAddMenu();
  }
  createEffect(() => {
    if (!addMenuOpen()) return;
    const onDocDown = () => closeAddMenu();
    // CAPTURE phase on window (2026-09-30, next-update-plan 1.8): this has to
    // run BEFORE the editor's own Esc - a delegated handler on its root that
    // closes the whole Timelines editor - and stop the key there. As a plain
    // window listener it ran after, so one Esc closed the menu AND the editor.
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.stopPropagation();
      ev.preventDefault();
      closeAddMenu();
    };
    const onScroll = () => {
      // Reposition on scroll/resize so the panel tracks the trigger.
      if (!activeTriggerEl) return;
      const rect = activeTriggerEl.getBoundingClientRect();
      setAddMenuPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    };
    window.addEventListener('pointerdown', onDocDown);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    onCleanup(() => {
      window.removeEventListener('pointerdown', onDocDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    });
  });

  /* Two-column layout so horizontal scroll is bounded to the tracks
     column and can never bleed under the sticky names column:
       .timeline-grid            outer — vertical scroll only
         .timeline-grid-gutter-col   fixed 140px, no horizontal scroll
         .timeline-grid-tracks-col   flex-1, owns horizontal scroll +
                                     the horizontal scrollbar
     The playhead + drop preview live inside .timeline-grid-tracks-col
     so their positions are naturally clipped to that column's overflow. */
  let tracksRef: HTMLDivElement | undefined;

  function onWheel(ev: WheelEvent) {
    if (ev.altKey) {
      ev.preventDefault();
      // Zoom-around-cursor: keep the bar under the pointer pinned to the
      // same viewport-x after the zoom step. Convention borrowed from
      // wavesurfer.js ZoomPlugin.
      const dir = ev.deltaY < 0 ? 1 : -1;
      const prevZoom = props.zoom;
      const nextZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number((prevZoom + dir * ZOOM_STEP).toFixed(2))));
      if (nextZoom === prevZoom) return;
      // `pxPerBar` scales linearly with zoom (see the pxPerBar memo in
      // SelectedDetail). Derive the new value proportionally instead of
      // re-deriving from containerW math we don't have access to here.
      const prevPxPerBar = props.pxPerBar;
      const nextPxPerBar = prevPxPerBar * (nextZoom / prevZoom);
      const rect = tracksRef?.getBoundingClientRect();
      const localX = rect ? Math.max(0, ev.clientX - rect.left) : 0;
      const scrollLeft = tracksRef?.scrollLeft ?? 0;
      // World-bar under the pointer BEFORE zoom.
      const worldBar = (scrollLeft + localX) / prevPxPerBar;
      props.setZoom(nextZoom);
      // Reposition scroll so the same bar sits under the pointer AFTER
      // zoom. Wait one frame so the layout reflows to the new pxPerBar
      // before we set scrollLeft — otherwise the container width lags.
      requestAnimationFrame(() => {
        if (!tracksRef) return;
        const targetScroll = worldBar * nextPxPerBar - localX;
        tracksRef.scrollLeft = Math.max(0, targetScroll);
      });
    } else if (ev.shiftKey) {
      if (!tracksRef) return;
      ev.preventDefault();
      // deltaY < 0 = wheel up = scroll left
      const dir = ev.deltaY < 0 ? -1 : 1;
      tracksRef.scrollLeft += dir * SHIFT_SCROLL_PX;
    }
  }

  const visible = () => t().lanes;

  return (
    <div
      class="timeline-grid"
      data-fit={props.fitMode ? 'true' : 'false'}
      style={{ '--px-per-bar': `${props.pxPerBar}px` }}
      onWheel={onWheel}
    >
      {/* LEFT — fixed gutter column. Corner label + one gutter per lane
          + add-lanes footer slot, all stacked so vertical scroll of the
          outer grid moves them in lockstep with the tracks column. */}
      <div class="timeline-grid-gutter-col">
        <div class="timeline-grid-gutter-head">
          <span class="timeline-grid-gutter-head-title">Lanes</span>
          <div class="timeline-grid-add-menu" data-open={addMenuOpen() ? 'true' : 'false'}>
            <button
              ref={addTriggerRef}
              type="button"
              class="timeline-grid-add-menu-trigger"
              onClick={(ev) => {
                ev.stopPropagation();
                if (addMenuOpen()) closeAddMenu();
                else openAddMenu();
              }}
              title="Add a lane"
              aria-label="Add a lane"
              aria-haspopup="menu"
              aria-expanded={addMenuOpen() ? 'true' : 'false'}
            >
              +
            </button>
            <Show when={addMenuOpen() && addMenuPos()}>
              <Portal>
                <div
                  class="timeline-grid-add-menu-panel"
                  role="menu"
                  onPointerDown={(ev) => ev.stopPropagation()}
                  style={{
                    top: `${addMenuPos()!.top}px`,
                    right: `${addMenuPos()!.right}px`,
                  }}
                >
                  <button type="button" role="menuitem" onClick={() => pickLane('midi')}>+ MIDI lane</button>
                  <button type="button" role="menuitem" onClick={() => pickLane('dmx-rig')}>+ DMX Rig lane</button>
                  <button type="button" role="menuitem" onClick={pickFixture}>+ DMX Fixture lane</button>
                  {/* No "+ OSC lane" / "+ Marker lane" (plan 3.3, 2026-10-01):
                      nothing in the editor makes a step that plays on either
                      (an OSC lane takes only Transpose/Capo values, which no
                      timeline can play yet; nothing creates a marker). They
                      come back with something to put on them. Existing ones
                      stay, and keep their steps. */}
                  <button type="button" role="menuitem" onClick={() => pickLane('mixed')}>+ Anything lane</button>
                </div>
              </Portal>
            </Show>
          </div>
        </div>
        <Index each={visible()}>
          {(lane) => (
            <LaneGutter
              timeline={t()}
              lane={lane}
              previewBar={props.previewBar}
              stepWarnings={stepWarnings()}
            />
          )}
        </Index>
        <div class="timeline-grid-gutter-foot" aria-hidden="true" />
      </div>

      {/* RIGHT — tracks column. Horizontal scroll lives here; its
          scrollbar can never extend under the gutter column. */}
      <div
        class="timeline-grid-tracks-col"
        ref={tracksRef}
        data-fit={props.fitMode ? 'true' : 'false'}
      >
        {/* Empty-timeline prompt — a fresh timeline seeds zero lanes
            (2026-08-04, operator asked for a blank slate); this replaces
            the old auto-seeded defaults with an explicit nudge instead
            of a silently empty grid. Overlays the scroll viewport (not
            tracks-inner) so it centers regardless of the 1-bar-wide
            content underneath. */}
        <Show when={visible().length === 0}>
          <div class="timeline-grid-empty">
            <p class="timeline-grid-empty-text">This timeline has no lanes yet.</p>
            <button
              type="button"
              class="timeline-grid-empty-add"
              ref={emptyAddTriggerRef}
              onClick={(ev) => {
                ev.stopPropagation();
                openAddMenuFrom(emptyAddTriggerRef);
              }}
            >
              + Add a lane
            </button>
          </div>
        </Show>
        <div
          class="timeline-grid-tracks-inner"
          style={{ width: `${gridPxWidth()}px` }}
        >
          {/* Ruler */}
          <div class="timeline-grid-ruler-wrap">
            <Ruler
              timeline={t()}
              pxPerBar={props.pxPerBar}
              showSections={props.showSections}
              armedBar={props.armedBar}
              onScrubToBar={props.onScrubToBar}
            />
          </div>

          {/* Track rows — one per visible lane */}
          <Index each={visible()}>
            {(lane) => (
              <LaneTrack
                timeline={t()}
                lane={lane}
                pxPerBar={props.pxPerBar}
                totalBars={totalB()}
                selectedStepId={props.selectedStepId}
                onSelectStep={props.onSelectStep}
                onScrubToPosition={props.onScrubToPosition}
                onFocusLane={props.onFocusLane}
                onCopy={props.onCopy}
                onCut={props.onCut}
                onPaste={props.onPaste}
                onDuplicate={props.onDuplicate}
                onPasteAtPosition={props.onPasteAtPosition}
                stepWarnings={stepWarnings()}
              />
            )}
          </Index>

          {/* Playhead — position is relative to the tracks-inner
              (which starts at bar 0), so no LANE_GUTTER_W offset. */}
          <div
            class="timeline-grid-playhead"
            style={{
              left: `${pxAt(Math.min(props.previewBar, totalB()), props.pxPerBar)}px`,
            }}
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

interface RulerProps {
  timeline: Timeline;
  pxPerBar: number;
  showSections: boolean;
  armedBar: number | null;
  onScrubToBar: (barIx: number) => void;
}

function Ruler(props: RulerProps) {
  const t = () => props.timeline;
  let rulerRef: HTMLDivElement | undefined;

  // Section bands (Song/Genre): render behind bar ticks, colored strip.
  const sectionBands = createMemo(() => {
    if (!props.showSections) return [];
    let bar = timelineLeadIn(t());
    return t().sections.map((s, i) => {
      const start = bar;
      bar += s.lengthBars;
      return { id: s.id, name: s.name, start, len: s.lengthBars, ix: i };
    });
  });

  const bars = createMemo(() => {
    const n = Math.max(1, totalBars(t()));
    const out: number[] = [];
    for (let i = 0; i < n; i++) out.push(i);
    return out;
  });

  function onRulerClick(ev: MouseEvent) {
    if (!rulerRef) return;
    // Ignore clicks on the section-name text — those already have their
    // own interaction (renaming lives elsewhere; here we just don't
    // consume the click for scrub since the name label sits on top).
    const target = ev.target as HTMLElement | null;
    if (target?.closest('.timeline-ruler-band-name')) return;
    const rect = rulerRef.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const barIx = Math.floor(x / props.pxPerBar);
    props.onScrubToBar(barIx);
  }

  return (
    <div
      class="timeline-ruler-hit"
      ref={rulerRef}
      onClick={onRulerClick}
      title="Click to move playhead - snaps to bar. When playing, jumps at the next bar boundary."
    >
      <Show when={props.showSections}>
        <div class="timeline-ruler-bands">
          <For each={sectionBands()}>
            {(band) => (
              <div
                class="timeline-ruler-band"
                data-ix={band.ix % 4}
                style={{
                  left: `${pxAt(band.start, props.pxPerBar)}px`,
                  width: `${pxAt(band.start + band.len, props.pxPerBar) - pxAt(band.start, props.pxPerBar)}px`,
                }}
                title={`${band.name} · ${band.len}b`}
              >
                <span class="timeline-ruler-band-name">{band.name}</span>
              </div>
            )}
          </For>
        </div>
      </Show>
      <div class="timeline-ruler-bars">
        <For each={bars()}>
          {(barIx) => {
            /** True while a marquee is active and its bar-range overlaps
             *  this bar (integer bar `barIx` covers [barIx, barIx+1)). */
            const inMarquee = () => {
              const r = marqueeBarRange();
              if (!r) return false;
              return r.fromBar < barIx + 1 && r.toBar >= barIx;
            };
            return (
              <div
                class="timeline-ruler-bar"
                data-in-marquee={inMarquee() ? 'true' : 'false'}
                style={{ left: `${pxAt(barIx, props.pxPerBar)}px` }}
              >
                <span class="timeline-ruler-bar-num">{barIx + 1}</span>
              </div>
            );
          }}
        </For>
      </div>
      <Show when={props.armedBar !== null}>
        <div
          class="timeline-ruler-armed"
          style={{
            left: `${pxAt(props.armedBar ?? 0, props.pxPerBar)}px`,
            width: `${pxAt((props.armedBar ?? 0) + 1, props.pxPerBar) - pxAt(props.armedBar ?? 0, props.pxPerBar)}px`,
          }}
          aria-hidden="true"
        />
      </Show>
    </div>
  );
}

/** Why NOTHING on a lane plays, or null (plan 3.3). A light lane is the case
 *  that needs it: its strip paints the colour across every bar, which reads
 *  as "the lights will do this" while no timeline can drive a light yet, and
 *  the dashed outline on one small chip was easy to miss (seen in the first
 *  render). Markers are left out: they are notes, never fired. Shared by the
 *  gutter (the ⊘) and the track (the dimmed strip) so the two cannot differ. */
function laneWontPlayReason(lane: TimelineLane, warnings: ReadonlyMap<string, string>): string | null {
  const steps = lane.steps.filter((s) => s.kind !== 'marker');
  if (steps.length === 0) return null;
  const reasons = steps.map((s) => warnings.get(s.id));
  if (reasons.some((r) => !r)) return null;
  return [...new Set(reasons)].join('; ');
}

interface LaneGutterProps {
  timeline: Timeline;
  /** Reactive accessor — `<Index>` yields one so the row stays mounted
   *  across store updates (typing in the name input keeps focus). */
  lane: () => TimelineLane;
  /** Current playhead position (fractional bars). The dmx-rig/dmx-fixture
   *  swatch samples the authored colour at this bar so it tracks playback. */
  previewBar: number;
  /** Step id -> why it won't play (plan 3.3). */
  stepWarnings: ReadonlyMap<string, string>;
}

function LaneGutter(props: LaneGutterProps) {
  const isColorCapable = () =>
    props.lane().laneKind === 'dmx-rig' || props.lane().laneKind === 'dmx-fixture';
  const gutterKindAttr = () => props.lane().laneKind ?? 'mixed';
  const laneWontPlay = () => laneWontPlayReason(props.lane(), props.stepWarnings);

  /** Every lane kind gets the right-click New/Duplicate menu EXCEPT
   *  `dmx-fixture` — "New Lane" there needs the fixture picker (which lane
   *  does it bind to?), not a blind add, so it keeps the browser/default
   *  menu. Duplicate stays legitimate for a fixture lane (store.ts docs
   *  it as "same fixture, different cue") but the quick-add entry doesn't
   *  make sense without picking a fixture first. */
  const laneKind = () => props.lane().laneKind ?? 'mixed';
  const isPlainLane = () => laneKind() !== 'dmx-fixture';

  /** Right-click menu anchor (viewport coords), null when closed. */
  const [laneMenu, setLaneMenu] = createSignal<{ x: number; y: number } | null>(null);

  function onGutterContextMenu(ev: MouseEvent) {
    if (!isPlainLane()) return;   // dmx-fixture keeps the browser/default menu
    ev.preventDefault();
    ev.stopPropagation();
    setLaneMenu({ x: ev.clientX, y: ev.clientY });
  }
  function onNewLane() {
    addLane(props.timeline.id, laneKind());
    setLaneMenu(null);
  }
  function onDuplicate() {
    duplicateLane(props.timeline.id, props.lane().id);
    setLaneMenu(null);
  }

  /** Two-step delete: first click arms (X hops to the left of the name);
   *  second click on the armed X actually removes. Clicking anywhere else
   *  or losing focus disarms. */
  const [armed, setArmed] = createSignal(false);

  function onRemoveClick(ev: MouseEvent) {
    ev.stopPropagation();
    if (armed()) {
      removeLane(props.timeline.id, props.lane().id);
    } else {
      setArmed(true);
    }
  }

  function disarm() {
    if (armed()) setArmed(false);
  }

  const dragDim = () => {
    const kind = paletteDragKind();
    if (!kind) return false;
    return !laneAcceptsStep(props.lane().laneKind, kind);
  };

  return (
    <div
      class="timeline-lane-gutter"
      data-kind={gutterKindAttr()}
      data-wont-play={laneWontPlay() ? 'true' : undefined}
      data-armed={armed() ? 'true' : 'false'}
      data-drag-dim={dragDim() ? 'true' : 'false'}
      onContextMenu={onGutterContextMenu}
      onPointerDown={(ev) => {
        // Any pointer down inside the gutter that ISN'T on the remove
        // button itself disarms.
        const el = ev.target as HTMLElement | null;
        if (!el?.closest('.timeline-lane-remove')) disarm();
      }}
    >
      <Show when={isColorCapable()}>
        <LaneOutputSwatch lane={props.lane()} samplePosBars={props.previewBar} />
      </Show>
      <Show when={laneWontPlay()}>
        {(why) => (
          <span
            class="timeline-lane-wont-play"
            title={`Nothing on this lane will play yet: ${why()}.`}
            aria-label="Nothing on this lane will play yet"
          >⊘</span>
        )}
      </Show>
      {/* Armed X sits BEFORE the name; unarmed X sits after. Same button
          rendered in one of two slots so the click chain doesn't reset. */}
      <Show when={armed()}>
        <button
          type="button"
          class="timeline-lane-remove"
          data-armed="true"
          data-position="left"
          onClick={onRemoveClick}
          title="Click again to confirm delete"
          aria-label="Confirm delete lane"
        >
          ×
        </button>
      </Show>
      <input
        class="timeline-lane-name"
        value={props.lane().name}
        onFocus={(ev) => {
          disarm();
          (ev.currentTarget as HTMLInputElement).dataset.focusValue = props.lane().name;
        }}
        onInput={(ev) =>
          preserveCaret(ev, (v) => renameLane(props.timeline.id, props.lane().id, v))
        }
        onBlur={(ev) => {
          const prev = (ev.currentTarget as HTMLInputElement).dataset.focusValue ?? '';
          commitLaneName(props.timeline.id, props.lane().id, prev);
        }}
        onKeyDown={(ev) => {
          const input = ev.currentTarget as HTMLInputElement;
          if (ev.key === 'Enter') {
            input.blur();
          } else if (ev.key === 'Escape') {
            ev.stopPropagation();
            const prev = input.dataset.focusValue ?? '';
            renameLane(props.timeline.id, props.lane().id, prev);
            input.blur();
          }
        }}
        spellcheck={false}
        aria-label={`Lane ${props.lane().name} name`}
      />
      <Show when={!armed()}>
        <button
          type="button"
          class="timeline-lane-remove"
          data-armed="false"
          data-position="right"
          onClick={onRemoveClick}
          title="Remove lane"
          aria-label="Remove lane"
        >
          ×
        </button>
      </Show>
      <Show when={laneMenu()}>
        {(pos) => (
          <LaneMenu
            x={pos().x}
            y={pos().y}
            kindLabel={PLAIN_LANE_MENU_LABEL[laneKind()] ?? ''}
            onNewLane={onNewLane}
            onDuplicate={onDuplicate}
            onDismiss={() => setLaneMenu(null)}
          />
        )}
      </Show>
    </div>
  );
}

/** Friendly kind label for the "New … Lane" menu entry. Blank for `mixed`
 *  (reads "New Lane"). `dmx-fixture` never reaches the menu (needs the
 *  fixture picker, not a blind add). */
const PLAIN_LANE_MENU_LABEL: Partial<Record<TimelineLaneKind, string>> = {
  midi: 'MIDI',
  'dmx-rig': 'DMX Rig',
  osc: 'OSC',
  marker: 'Marker',
  mixed: '',
};

/** Right-click menu on a plain lane's gutter: add another lane of the same
 *  kind, or duplicate this lane with its steps. Mirrors the KeyframeMenu Portal
 *  pattern (backdrop dismiss + viewport-anchored). */
function LaneMenu(props: {
  x: number;
  y: number;
  kindLabel: string;
  onNewLane: () => void;
  onDuplicate: () => void;
  onDismiss: () => void;
}) {
  return (
    <Portal>
      <div
        class="timeline-color-menu-backdrop"
        onClick={props.onDismiss}
        onContextMenu={(ev) => { ev.preventDefault(); props.onDismiss(); }}
      />
      <div
        class="timeline-color-menu timeline-lane-menu"
        style={{ top: `${props.y + 4}px`, left: `${props.x + 4}px` }}
        onClick={(ev) => ev.stopPropagation()}
        onPointerDown={(ev) => ev.stopPropagation()}
        onPointerUp={(ev) => ev.stopPropagation()}
      >
        <button type="button" class="timeline-color-menu-item" onClick={props.onNewLane}>
          New {props.kindLabel ? `${props.kindLabel} ` : ''}Lane
        </button>
        <button type="button" class="timeline-color-menu-item" onClick={props.onDuplicate}>
          Duplicate Lane
        </button>
      </div>
    </Portal>
  );
}

/** Small color chip on a `dmx-rig`/`dmx-fixture` gutter that shows the
 *  lane's currently-authored colour output at the playhead. */
function LaneOutputSwatch(props: {
  lane: TimelineLane;
  /** Bar position to sample. Tracks the playhead in real time. */
  samplePosBars: number;
}) {
  const swatch = createMemo(() => laneSwatchColor(props.lane, props.samplePosBars));
  return (
    <Show when={swatch()}>
      <span
        class="timeline-fixture-swatch"
        style={{ background: swatch()! }}
        title="Authored colour at the playhead"
        aria-hidden="true"
      />
    </Show>
  );
}

interface LaneTrackProps {
  timeline: Timeline;
  lane: () => TimelineLane;
  pxPerBar: number;
  totalBars: number;
  selectedStepId: string | null;
  onSelectStep: (id: string | null) => void;
  /** Click-in-empty-space callback — beat-snapped fractional bar. */
  onScrubToPosition: (positionBars: number) => void;
  /** Called on pointerdown so the parent tracks which lane the pointer
   *  is on (used for Ctrl+A "select all on lane"). */
  onFocusLane: (laneId: string | null) => void;
  /** Clipboard operations delegated to SelectedDetail (the copy/paste
   *  logic needs cross-lane visibility + playhead position). */
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
  /** Right-click-empty-lane → paste at click position. `bar` is the
   *  fractional-bar under the cursor; `laneId` is THIS lane so the
   *  routing overrides the source-lane-first preference. */
  onPasteAtPosition: (laneId: string, bar: number) => void;
  /** Step id -> why it won't play (plan 3.3), for the "won't play" mark. */
  stepWarnings: ReadonlyMap<string, string>;
}

function LaneTrack(props: LaneTrackProps) {
  const isColorCapable = () =>
    props.lane().laneKind === 'dmx-rig' || props.lane().laneKind === 'dmx-fixture';

  // Color picker open for a specific marker: { stepId } or null. Position
  // shares the last right-click coords so it appears where the user
  // clicked. `stepId` = null means "pending" — no marker exists yet; the
  // first color the user picks creates it at `pendingPosition`. Right-
  // click on empty colour-capable lane opens the picker in pending mode;
  // if the user dismisses without picking, nothing is created.
  const [colorPicker, setColorPicker] = createSignal<{
    stepId: string | null;
    pendingPosition: number | null;
    x: number;
    y: number;
  } | null>(null);

  const [dragOverBar, setDragOverBar] = createSignal<number | null>(null);
  let rowRef: HTMLDivElement | undefined;

  function positionFromEvent(ev: DragEvent | MouseEvent): number {
    if (!rowRef) return 0;
    const rect = rowRef.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const raw = x / props.pxPerBar;
    const sigNum = props.timeline.signature?.num ?? 4;
    return snapToBeat(raw, sigNum);
  }

  function laneAcceptsCurrentDrag(): boolean {
    const dragKind = paletteDragKind();
    if (!dragKind) return false;
    return laneAcceptsStep(props.lane().laneKind, dragKind);
  }

  function onDragOver(ev: DragEvent) {
    if (!ev.dataTransfer) return;
    if (!laneAcceptsCurrentDrag()) {
      // Signal reject: browser draws the ⊘ cursor, no drop preview.
      ev.dataTransfer.dropEffect = 'none';
      return;
    }
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'copy';
    setDragOverBar(positionFromEvent(ev));
  }

  function onDragLeave() {
    setDragOverBar(null);
  }

  function onDrop(ev: DragEvent) {
    ev.preventDefault();
    setDragOverBar(null);
    if (!ev.dataTransfer) return;
    const raw = ev.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    let payload: PaletteDragPayload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    // Backward-compat: pre-existing payloads have no stepKind.
    const stepKind = payload.stepKind ?? 'buttonRef';
    if (!laneAcceptsStep(props.lane().laneKind, stepKind)) return;
    const position = positionFromEvent(ev);
    if (stepKind === 'colorMarker' && payload.color) {
      const step = makeColorMarkerStep(position, payload.color, {
        label: payload.labelHint,
      });
      addStep(props.timeline.id, props.lane().id, step);
      return;
    }
    if (stepKind === 'targetValue') {
      if (!payload.targetId || payload.value === undefined) return;
      const step = makeTargetValueStep(payload.targetId, payload.value, position, payload.labelHint);
      // A dropped block wins over what it lands on (2026-09-29, DAW overwrite).
      addStep(props.timeline.id, props.lane().id, step, { overwrite: true });
      return;
    }
    if (!payload.buttonId) return;
    const step = makeButtonRefStep(payload.buttonId, position, payload.labelHint);
    addStep(props.timeline.id, props.lane().id, step, { overwrite: true });
  }

  // Right-click menu for a colour-marker keyframe: "Pick color…" +
  // curve/space/path + cross-kind Copy/Cut entries.
  const [keyframeMenu, setKeyframeMenu] = createSignal<{
    stepId: string;
    stepKind: TimelineStep['kind'];
    x: number;
    y: number;
  } | null>(null);

  // Right-click on empty lane space → small "Paste at click position"
  // menu. Distinct from keyframeMenu because it has no step target and
  // its behaviour needs the click position (bar) + this lane id — the
  // paste routes to THIS lane (preferLaneId override) at THIS position
  // (playheadOverride) rather than at the timeline playhead.
  const [emptyMenu, setEmptyMenu] = createSignal<{
    barAtClick: number;
    x: number;
    y: number;
  } | null>(null);

  function positionFromClientX(clientX: number): number {
    if (!rowRef) return 0;
    const rect = rowRef.getBoundingClientRect();
    const raw = (clientX - rect.left) / props.pxPerBar;
    return Math.max(0, raw);
  }

  function findLocalKeyframeAt(target: HTMLElement | null): { stepId: string; kind: 'colorMarker' } | null {
    if (!target) return null;
    const cm = target.closest('.timeline-color-marker') as HTMLElement | null;
    if (cm) {
      const id = cm.dataset.stepId;
      if (id) return { stepId: id, kind: 'colorMarker' };
    }
    return null;
  }

  const CLICK_DRAG_PX = 4;

  /** Single pointerdown entry point. Distinguishes click vs drag by
   *  distance threshold; routes to marquee, scrub, add-keyframe, drag-
   *  keyframe, or step-block passthrough depending on hit. */
  function onPointerDown(ev: PointerEvent) {
    // Focus tracking for Ctrl+A on lane.
    props.onFocusLane(props.lane().id);

    // Only left-button. Right = context menu (handled by onContextMenu
    // on the step children); middle = ignored.
    if (ev.button !== 0) return;
    // Ignore drag-initiators from step blocks (buttonRef drops) — they
    // handle their own move + resize.
    const target = ev.target as HTMLElement | null;
    if (target?.closest('.timeline-step-block')) return;

    // Keyframe hit? Route to select + drag.
    const hit = findLocalKeyframeAt(target);
    if (hit) {
      onKeyframePointerDown(ev, hit);
      return;
    }

    // Empty space — click-vs-drag decision. Start a listener; if the
    // pointer moves past threshold, upgrade to marquee. Otherwise commit
    // as a click on release.
    const startX = ev.clientX;
    const startY = ev.clientY;
    let isMarquee = false;

    const move = (e: PointerEvent) => {
      if (isMarquee) {
        marqueeUpdate(e);
        return;
      }
      if (Math.abs(e.clientX - startX) > CLICK_DRAG_PX ||
          Math.abs(e.clientY - startY) > CLICK_DRAG_PX) {
        isMarquee = true;
        marqueeStart(startX, startY, ev.shiftKey);
        marqueeUpdate(e);
      }
    };
    const up = (e: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (isMarquee) {
        marqueeEnd();
        return;
      }
      // Committed click.
      onEmptyClickCommit(e);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** Left-click on empty space — fires when pointerdown didn't move enough
   *  to be a marquee. Scrubs the playhead. Colour-marker creation off-line
   *  is a double-click gesture (see `onKeyframeDoubleClick`). */
  function onEmptyClickCommit(ev: PointerEvent) {
    const rawBars = positionFromClientX(ev.clientX);
    const snapped = snapToBeat(rawBars, props.timeline.signature?.num ?? 4);

    props.onScrubToPosition(snapped);
    // Empty-space click also clears any live selection.
    clearSelection();
    props.onSelectStep(null);
  }

  /** Pointerdown on a keyframe (value point or color marker). Updates
   *  selection (click / shift-add / cmd-toggle), then wires a drag: value
   *  points drag vertically to change value; color markers drag
   *  horizontally to reposition. Also handles double-click delete. */
  function onKeyframePointerDown(ev: PointerEvent, hit: { stepId: string; kind: 'colorMarker' }) {
    // Open the undo batch here so both an Alt-duplicate AND the drag
    // that follows land in one entry. Closes in the shared `up` handler.
    beginBatch();

    // Alt+drag = duplicate the source in place, then drag the CLONE.
    // The original stays put; the clone is what the drag re-positions.
    // Matches Ableton/Reaper muscle memory.
    if (ev.altKey) {
      const cloneId = duplicateStep(props.timeline.id, props.lane().id, hit.stepId);
      if (cloneId) {
        // Retarget the drag to the clone; selection follows.
        hit = { ...hit, stepId: cloneId };
        setSelection([selKey(props.lane().id, cloneId)]);
        props.onSelectStep(cloneId);
      }
    }

    // Selection semantics (skipped when Alt already set selection to
    // the clone).
    if (!ev.altKey) {
      if (ev.shiftKey) {
        addToSelection(props.lane().id, hit.stepId);
      } else if (ev.ctrlKey || ev.metaKey) {
        toggleInSelection(props.lane().id, hit.stepId);
      } else if (!isSelected(props.lane().id, hit.stepId)) {
        setSelection([selKey(props.lane().id, hit.stepId)]);
      }
    }
    props.onSelectStep(hit.stepId);

    // Drag: batched so N mousemoves collapse to ONE undo entry. Colour
    // markers are X-only (no Y axis to move — beat position only).
    let dragged = false;
    // (beginBatch already fired at the top of this handler.)
    const startX = ev.clientX;
    const startY = ev.clientY;
    /** null = freeform; 'x' or 'y' = axis-lock active. */
    let axisLock: 'x' | 'y' | null = null;
    /** True on the previous frame — used to detect the Shift-down edge. */
    let prevShift = false;
    /** Last snapped bar position we sent to `moveStep`, so we can pick
     *  the approach direction when the moved keyframe lands on an
     *  already-occupied beat: rightward drag → sit on the right,
     *  leftward → sit on the left. Seeded from the step's current
     *  position so the very first tick has an anchor. */
    let lastSnappedX: number | null = (() => {
      const step = props.lane().steps.find((s) => s.id === hit.stepId);
      return step?.position ?? null;
    })();
    // Pointer capture so the drag stays attached to the source element
    // even if the pointer leaves the row.
    const captureTarget = ev.target as Element | null;
    let capturedPointerId: number | null = null;
    try {
      if (captureTarget && 'setPointerCapture' in captureTarget) {
        (captureTarget as Element & { setPointerCapture: (id: number) => void })
          .setPointerCapture(ev.pointerId);
        capturedPointerId = ev.pointerId;
      }
    } catch {
      // Some browsers throw if the target is detached mid-flow; fall
      // back to window-level listeners, which we're already using.
    }

    const move = (e: PointerEvent) => {
      if (!dragged) {
        if (Math.abs(e.clientX - startX) < 2 && Math.abs(e.clientY - startY) < 2) return;
        dragged = true;
        // First real motion — hide the cursor. Restored on pointerup.
        document.body.classList.add('timeline-keyframe-dragging');
      }
      // Axis-lock state machine: Shift-down edge samples the winning
      // axis at that instant; Shift-up releases the lock entirely.
      const shift = e.shiftKey;
      if (shift && !prevShift) {
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        axisLock = dx >= dy ? 'x' : 'y';
      } else if (!shift && prevShift) {
        axisLock = null;
      }
      prevShift = shift;

      // X-only: colour markers have no Y axis. Shift-lock to Y is a no-op.
      if (axisLock === 'y') return;
      const raw = positionFromClientX(e.clientX);
      const snapped = snapToBeat(raw, props.timeline.signature?.num ?? 4);
      // Only write when the snapped bar changes — collapses 60Hz
      // mousemoves into per-beat updates. Dragging rightward
      // (snapped > last) came FROM the left, so settle on the LEFT
      // of any occupant. Leftward = came FROM the right = RIGHT.
      if (snapped !== lastSnappedX) {
        const dir: 'left' | 'right' =
          lastSnappedX === null || snapped > lastSnappedX ? 'left' : 'right';
        moveStep(props.timeline.id, props.lane().id, hit.stepId, snapped, dir);
        lastSnappedX = snapped;
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      // Release pointer capture + restore cursor. Guard the capture
      // release so a stale capture (target unmounted mid-drag) doesn't
      // throw.
      if (capturedPointerId !== null && captureTarget && 'releasePointerCapture' in captureTarget) {
        try {
          (captureTarget as Element & { releasePointerCapture: (id: number) => void })
            .releasePointerCapture(capturedPointerId);
        } catch {
          // ignore
        }
      }
      document.body.classList.remove('timeline-keyframe-dragging');
      endBatch();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** Double-click on a keyframe deletes it (and any other selected
   *  keyframes). */
  function onKeyframeDoubleClick(ev: MouseEvent) {
    const target = ev.target as HTMLElement | null;
    const hit = findLocalKeyframeAt(target);
    if (!hit) {
      // Double-click on empty space of a colour-capable lane opens the
      // picker in pending mode at the snapped beat. A translucent ghost
      // marker renders at that position until the user picks a color
      // (commits) or dismisses (ghost vanishes). Single-click scrubs on
      // every lane, so keyframe creation is moved onto the double-click
      // gesture.
      if (isColorCapable()) {
        ev.preventDefault();
        ev.stopPropagation();
        const position = snapToBeat(positionFromClientX(ev.clientX), props.timeline.signature?.num ?? 4);
        setColorPicker({
          stepId: null,
          pendingPosition: position,
          x: ev.clientX,
          y: ev.clientY,
        });
      }
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    // If nothing else is selected, delete just this one; otherwise the
    // selection has priority (already includes this one from pointerdown).
    const sel = selection();
    if (sel.size === 0) {
      removeStep(props.timeline.id, props.lane().id, hit.stepId);
    } else {
      const pairs = [...sel].map((k) => parseSelKey(k));
      removeSteps(props.timeline.id, pairs);
      clearSelection();
    }
    props.onSelectStep(null);
  }

  function onKeyframeContextMenu(ev: MouseEvent) {
    const target = ev.target as HTMLElement | null;
    const hit = findLocalKeyframeAt(target);
    if (hit) {
      ev.preventDefault();
      ev.stopPropagation();
      setKeyframeMenu({ stepId: hit.stepId, stepKind: hit.kind, x: ev.clientX, y: ev.clientY });
      // Make sure this keyframe is selected so menu operations target it.
      if (!isSelected(props.lane().id, hit.stepId)) {
        setSelection([selKey(props.lane().id, hit.stepId)]);
        props.onSelectStep(hit.stepId);
      }
      return;
    }
    // Empty-space right-click: open the small paste-at-position menu.
    // Left-click on empty space stays constructive (adds a value point,
    // opens color picker, or scrubs, depending on lane kind) — this is
    // strictly a right-click affordance.
    ev.preventDefault();
    ev.stopPropagation();
    const bar = positionFromClientX(ev.clientX);
    setEmptyMenu({ barAtClick: bar, x: ev.clientX, y: ev.clientY });
  }

  function menuPickColor() {
    const m = keyframeMenu();
    if (!m || m.stepKind !== 'colorMarker') return;
    setColorPicker({ stepId: m.stepId, pendingPosition: null, x: m.x, y: m.y });
    setKeyframeMenu(null);
  }
  function menuApplyCurve(curve: ValuePointCurve) {
    const m = keyframeMenu();
    if (!m) return;
    // Apply the curve to every selected keyframe (matches the marquee →
    // right-click → change-all flow). If nothing is selected — shouldn't
    // happen since the menu opener guarantees selection — fall back to
    // just the menu-target.
    const menuKey = selKey(props.lane().id, m.stepId);
    const keys = new Set<string>(selection());
    keys.add(menuKey);
    beginBatch();
    try {
      for (const k of keys) {
        const { laneId, stepId } = parseSelKey(k);
        const lane = props.timeline.lanes.find((l) => l.id === laneId);
        if (!lane) continue;
        const step = lane.steps.find((s) => s.id === stepId);
        if (!step || step.kind !== 'colorMarker') continue;
        setColorMarkerCurve(props.timeline.id, laneId, stepId, curve);
      }
    } finally {
      endBatch();
    }
    setKeyframeMenu(null);
  }
  /** Apply an interpolation space (sRGB/HSL/OKLCH) to every selected
   *  color marker. Value points are ignored — space only makes sense on
   *  color markers. */
  function menuApplySpace(space: ColorInterpolationSpace) {
    const m = keyframeMenu();
    if (!m || m.stepKind !== 'colorMarker') return;
    const menuKey = selKey(props.lane().id, m.stepId);
    const keys = new Set<string>(selection());
    keys.add(menuKey);
    beginBatch();
    try {
      for (const k of keys) {
        const { laneId, stepId } = parseSelKey(k);
        const lane = props.timeline.lanes.find((l) => l.id === laneId);
        if (!lane) continue;
        const step = lane.steps.find((s) => s.id === stepId);
        if (!step || step.kind !== 'colorMarker') continue;
        setColorMarkerSpace(props.timeline.id, laneId, stepId, space);
      }
    } finally {
      endBatch();
    }
    setKeyframeMenu(null);
  }
  /** Apply a hue-wheel direction to every selected color marker. Ignored
   *  on markers whose current space is sRGB (no wheel to walk). */
  function menuApplyPath(path: HuePathDirection) {
    const m = keyframeMenu();
    if (!m || m.stepKind !== 'colorMarker') return;
    const menuKey = selKey(props.lane().id, m.stepId);
    const keys = new Set<string>(selection());
    keys.add(menuKey);
    beginBatch();
    try {
      for (const k of keys) {
        const { laneId, stepId } = parseSelKey(k);
        const lane = props.timeline.lanes.find((l) => l.id === laneId);
        if (!lane) continue;
        const step = lane.steps.find((s) => s.id === stepId);
        if (!step || step.kind !== 'colorMarker') continue;
        setColorMarkerPath(props.timeline.id, laneId, stepId, path);
      }
    } finally {
      endBatch();
    }
    setKeyframeMenu(null);
  }
  function menuDelete() {
    const m = keyframeMenu();
    if (!m) return;
    // When the menu opens, `onKeyframeContextMenu` guarantees the
    // right-clicked marker is in the selection: if it wasn't, selection
    // was overwritten to just this one. So `selection()` is always the
    // intended delete set. Union in the menu-target defensively in case
    // the two ever diverge (async selection updates, etc.).
    const menuKey = selKey(props.lane().id, m.stepId);
    const sel = selection();
    const keys = new Set<string>(sel);
    keys.add(menuKey);
    const pairs = [...keys].map((k) => parseSelKey(k));
    removeSteps(props.timeline.id, pairs);
    clearSelection();
    props.onSelectStep(null);
    setKeyframeMenu(null);
  }

  function applyPickedColor(stepId: string, rgb: { r: number; g: number; b: number }) {
    setColorMarkerColor(props.timeline.id, props.lane().id, stepId, rgb);
  }

  /** Picker → marker bridge. If the picker is in "pending" mode
   *  (`stepId === null`), the first color change materializes a new
   *  marker at `pendingPosition`, then subsequent changes update it
   *  in-place. If the marker already exists, just update it. */
  function onPickerColorChange(rgb: { r: number; g: number; b: number }) {
    const pk = colorPicker();
    if (!pk) return;
    if (pk.stepId) {
      applyPickedColor(pk.stepId, rgb);
      return;
    }
    if (pk.pendingPosition === null) return;
    const step = makeColorMarkerStep(pk.pendingPosition, rgb);
    const id = addStep(props.timeline.id, props.lane().id, step);
    if (id) {
      // Promote the picker to "editing existing marker" mode so
      // subsequent slider drags update the same one instead of stacking
      // new markers.
      setColorPicker({ stepId: id, pendingPosition: null, x: pk.x, y: pk.y });
    }
  }

  function onStepContextMenu(ev: MouseEvent, stepId: string) {
    // Right-click on a step block (buttonRef, cue, marker, animation,
    // sequencer, macro): open the shared context menu with
    // Cut / Copy / Paste / Duplicate / Delete. Selects the target if
    // not already selected so menu operations target it.
    ev.preventDefault();
    ev.stopPropagation();
    const lane = props.lane();
    const step = lane.steps.find((s) => s.id === stepId);
    if (!step) return;
    if (!isSelected(lane.id, stepId)) {
      setSelection([selKey(lane.id, stepId)]);
      props.onSelectStep(stepId);
    }
    setKeyframeMenu({ stepId, stepKind: step.kind, x: ev.clientX, y: ev.clientY });
  }

  // ─── Marquee selection ────────────────────────────────────────────

  const [marquee, setMarquee] = createSignal<{
    /** Origin in viewport coords. */
    x0: number; y0: number;
    /** Current pointer in viewport coords. */
    x1: number; y1: number;
    /** Selection at marquee start — additive marquees union with this. */
    baseline: Set<string>;
    additive: boolean;
    /** True while the top edge is at or above the ruler. When true the
     *  marquee draws a vertical column covering every lane between
     *  min/maxX, matching the whole-column selection behaviour. */
    columnMode: boolean;
    /** Cached tracks-inner rect when in column mode — the render pulls
     *  its top/bottom from here so the visual matches the selection
     *  math exactly. */
    columnBounds: { top: number; bottom: number } | null;
  } | null>(null);

  function marqueeStart(x0: number, y0: number, shift: boolean) {
    setMarquee({
      x0, y0, x1: x0, y1: y0,
      baseline: shift ? new Set(selection()) : new Set(),
      additive: shift,
      columnMode: false,
      columnBounds: null,
    });
    if (!shift) clearSelection();
    // Kill text-selection body-wide while dragging — the pointer may
    // travel outside the timeline surface, and the browser will otherwise
    // paint highlights on any text it crosses.
    document.body.classList.add('timeline-marquee-active');
    // Drop any existing selection the browser might have painted before
    // we noticed the drag.
    if (typeof window !== 'undefined') window.getSelection()?.removeAllRanges();
  }
  function marqueeUpdate(ev: PointerEvent) {
    const m = marquee();
    if (!m) return;
    // Detect column-mode from the fresh y-range and cache the column
    // bounds so the render + recompute agree without re-measuring.
    const nextMinY = Math.min(m.y0, ev.clientY);
    const ruler = document.querySelector<HTMLElement>('.timeline-grid-ruler-wrap');
    const tracksInner = document.querySelector<HTMLElement>('.timeline-grid-tracks-inner');
    let columnMode = false;
    let columnBounds: { top: number; bottom: number } | null = null;
    if (ruler && tracksInner) {
      const rulerRect = ruler.getBoundingClientRect();
      if (nextMinY <= rulerRect.bottom) {
        columnMode = true;
        const innerRect = tracksInner.getBoundingClientRect();
        columnBounds = { top: innerRect.top, bottom: innerRect.bottom };
      }
    }
    setMarquee({ ...m, x1: ev.clientX, y1: ev.clientY, columnMode, columnBounds });
    recomputeMarqueeSelection();
    // Publish the marquee's x-range as fractional bars so the Ruler can
    // highlight bar numbers whose bar overlaps. Use the tracks-inner
    // element to convert viewport-x → fractional-bar.
    if (tracksInner) {
      const innerRect = tracksInner.getBoundingClientRect();
      const minX = Math.min(m.x0, ev.clientX);
      const maxX = Math.max(m.x0, ev.clientX);
      const fromBar = Math.max(0, (minX - innerRect.left) / props.pxPerBar);
      const toBar = Math.max(0, (maxX - innerRect.left) / props.pxPerBar);
      setMarqueeBarRange({ fromBar, toBar });
    }
  }
  function marqueeEnd() {
    recomputeMarqueeSelection();
    setMarquee(null);
    setMarqueeBarRange(null);
    document.body.classList.remove('timeline-marquee-active');
  }
  function recomputeMarqueeSelection() {
    const m = marquee();
    if (!m) return;
    const minX = Math.min(m.x0, m.x1);
    const maxX = Math.max(m.x0, m.x1);
    let minY = Math.min(m.y0, m.y1);
    let maxY = Math.max(m.y0, m.y1);
    // Column-select mode: bounds come from the cached tracks-inner rect
    // captured in marqueeUpdate. Selects every keyframe in the horizontal
    // slice regardless of lane, so the user can drag up into the ruler
    // to grab a whole beat-range in one gesture.
    if (m.columnMode && m.columnBounds) {
      minY = m.columnBounds.top;
      maxY = m.columnBounds.bottom;
    }
    const inside = new Set<string>(m.baseline);
    // Query colour-marker keyframes AND step blocks (buttonRef, cue,
    // marker, animation, sequencer, macro) so the marquee grabs everything
    // on the visible tracks column. Hit test = any-overlap (AABB
    // intersection), not center-inside — even 1px of the block inside the
    // marquee counts as selected. Matches Ableton/Bitwig/Figma expectations.
    const scope = document.querySelectorAll<HTMLElement>(
      '.timeline-color-marker, .timeline-step-block',
    );
    for (const el of scope) {
      const r = el.getBoundingClientRect();
      const overlaps =
        r.right  >= minX && r.left   <= maxX &&
        r.bottom >= minY && r.top    <= maxY;
      if (overlaps) {
        const laneEl = el.closest<HTMLElement>('.timeline-lane-track');
        const laneId = laneEl?.dataset.laneId;
        const stepId = el.dataset.stepId;
        if (laneId && stepId) inside.add(selKey(laneId, stepId));
      }
    }
    setSelection(inside);
  }

  const kindAttr = () => props.lane().laneKind ?? 'mixed';

  const dragDim = () => {
    const kind = paletteDragKind();
    if (!kind) return false;
    return !laneAcceptsStep(props.lane().laneKind, kind);
  };

  return (
    <div
      class="timeline-lane-track"
      data-kind={kindAttr()}
      data-lane-id={props.lane().id}
      data-wont-play={laneWontPlayReason(props.lane(), props.stepWarnings) ? 'true' : undefined}
      data-drag-dim={dragDim() ? 'true' : 'false'}
      ref={rowRef}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onPointerDown={onPointerDown}
      onDblClick={onKeyframeDoubleClick}
      onContextMenu={onKeyframeContextMenu}
      style={{ width: `${pxAt(props.totalBars, props.pxPerBar)}px` }}
    >
      <BeatGrid totalBars={props.totalBars} pxPerBar={props.pxPerBar} sigNum={props.timeline.signature?.num ?? 4} />

      <Show when={isColorCapable()}>
        <ColorLaneStrip
          lane={props.lane()}
          pxPerBar={props.pxPerBar}
          totalBars={props.totalBars}
        />
      </Show>

      {/* Ghost marker — visible while the picker is in pending mode on
          this lane. Sits at the pendingPosition and looks like a real
          marker but dashed + translucent so the operator can see WHERE
          the marker will land if they commit a color. Vanishes when the
          picker dismisses. */}
      <Show
        when={
          isColorCapable() &&
          colorPicker() !== null &&
          colorPicker()!.stepId === null &&
          colorPicker()!.pendingPosition !== null
        }
      >
        <div
          class="timeline-color-marker-ghost"
          style={{
            left: `${pxAt(colorPicker()!.pendingPosition!, props.pxPerBar)}px`,
            width: `${
              pxAt(
                colorPicker()!.pendingPosition! + 1 / Math.max(1, props.timeline.signature?.num ?? 4),
                props.pxPerBar,
              ) - pxAt(colorPicker()!.pendingPosition!, props.pxPerBar)
            }px`,
          }}
          aria-hidden="true"
        />
      </Show>

      <For each={props.lane().steps}>
        {(step) => (
          <Show
            when={isColorCapable() && isColorMarkerStep(step)}
            fallback={
              <StepBlock
                timelineId={props.timeline.id}
                laneId={props.lane().id}
                step={step}
                pxPerBar={props.pxPerBar}
                sigNum={props.timeline.signature?.num ?? 4}
                totalBars={props.totalBars}
                selected={
                  props.selectedStepId === step.id ||
                  isSelected(props.lane().id, step.id)
                }
                onSelect={() => {
                  // A click on a block that is not already selected makes it
                  // THE selection, as a click on a colour marker does. Before
                  // 2026-10-01 a block click left the multi-selection alone, so
                  // after Ctrl+D then Ctrl+Z it still held the undone copies
                  // and Ctrl+C on the clicked block copied nothing.
                  if (!isSelected(props.lane().id, step.id)) setSelection([selKey(props.lane().id, step.id)]);
                  props.onSelectStep(step.id);
                }}
                onContextMenu={(ev) => onStepContextMenu(ev, step.id)}
                wontPlay={props.stepWarnings.get(step.id) ?? null}
              />
            }
          >
            <ColorMarkerChip
              laneId={props.lane().id}
              step={step as ColorMarkerStep}
              pxPerBar={props.pxPerBar}
              sigNum={props.timeline.signature?.num ?? 4}
              wontPlay={props.stepWarnings.get(step.id) ?? null}
            />
          </Show>
        )}
      </For>

      <Show when={dragOverBar() !== null}>
        <div
          class="timeline-lane-drop-preview"
          style={{ left: `${pxAt(dragOverBar() ?? 0, props.pxPerBar)}px` }}
          aria-hidden="true"
        />
      </Show>

      <Show when={marquee()}>
        {(m) => (
          <Portal>
            <div
              class="timeline-marquee"
              data-column-mode={m().columnMode ? 'true' : 'false'}
              style={{
                left: `${Math.min(m().x0, m().x1)}px`,
                top: `${m().columnMode && m().columnBounds ? m().columnBounds!.top : Math.min(m().y0, m().y1)}px`,
                width: `${Math.abs(m().x1 - m().x0)}px`,
                height: `${m().columnMode && m().columnBounds ? (m().columnBounds!.bottom - m().columnBounds!.top) : Math.abs(m().y1 - m().y0)}px`,
              }}
              aria-hidden="true"
            />
          </Portal>
        )}
      </Show>

      <Show when={keyframeMenu()}>
        {(menu) => (
          <KeyframeMenu
            x={menu().x}
            y={menu().y}
            showPickColor={menu().stepKind === 'colorMarker'}
            showCurve={menu().stepKind === 'colorMarker'}
            showColorInterpolation={menu().stepKind === 'colorMarker'}
            hasClipboard={hasClipboard()}
            onPickColor={menuPickColor}
            onCurve={menuApplyCurve}
            onSpace={menuApplySpace}
            onPath={menuApplyPath}
            onCopy={() => { props.onCopy(); setKeyframeMenu(null); }}
            onCut={() => { props.onCut(); setKeyframeMenu(null); }}
            onPaste={() => { props.onPaste(); setKeyframeMenu(null); }}
            onDuplicate={() => { props.onDuplicate(); setKeyframeMenu(null); }}
            onDelete={menuDelete}
            onDismiss={() => setKeyframeMenu(null)}
          />
        )}
      </Show>

      <Show when={emptyMenu()}>
        {(menu) => (
          <EmptyLanePasteMenu
            x={menu().x}
            y={menu().y}
            hasClipboard={hasClipboard()}
            onPaste={() => {
              const m = menu();
              props.onPasteAtPosition(props.lane().id, m.barAtClick);
              setEmptyMenu(null);
            }}
            onDismiss={() => setEmptyMenu(null)}
          />
        )}
      </Show>
      <Show when={colorPicker()}>
        {(pk) => {
          const step = pk().stepId
            ? props.lane().steps.find((s) => s.id === pk().stepId)
            : undefined;
          const current = step && isColorMarkerStep(step)
            ? step.color
            : { r: 1, g: 1, b: 1 };
          return (
            <ColorPicker
              x={pk().x}
              y={pk().y}
              initial={current}
              onChange={(rgb) => onPickerColorChange(rgb)}
              onDismiss={() => setColorPicker(null)}
            />
          );
        }}
      </Show>
    </div>
  );
}

// =====================================================================
// COLOR LANE STRIP — connecting-bar between consecutive color markers,
// filled with the interpolated color so the whole segment is visible.
// =====================================================================

function ColorLaneStrip(props: { lane: TimelineLane; pxPerBar: number; totalBars: number }) {
  const width = () => pxAt(props.totalBars, props.pxPerBar);
  const markers = createMemo(() => {
    return props.lane.steps
      .filter(isColorMarkerStep)
      .slice()
      .sort((a, b) => a.position - b.position);
  });
  // Reuse `sampleColorLane` at each pixel column would be overkill; we
  // draw a CSS `linear-gradient` per segment instead — the browser handles
  // the interpolation for free.
  const segments = createMemo(() => {
    const pts = markers();
    const out: Array<{ leftPx: number; widthPx: number; from: string; to: string; hold: boolean }> = [];
    if (pts.length === 0) return out;
    // Lead-in: flat hold from 0 to first marker.
    const firstX = pxAt(pts[0]!.position, props.pxPerBar);
    if (firstX > 0) {
      const c = rgbToCss(colorMarker01ToU8(pts[0]!.color));
      out.push({ leftPx: 0, widthPx: firstX, from: c, to: c, hold: true });
    }
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const leftPx = pxAt(a.position, props.pxPerBar);
      // End-minus-start so adjacent segments butt exactly.
      const widthPx = pxAt(b.position, props.pxPerBar) - leftPx;
      const from = rgbToCss(colorMarker01ToU8(a.color));
      const to = a.curve === 'hold' ? from : rgbToCss(colorMarker01ToU8(b.color));
      out.push({ leftPx, widthPx, from, to, hold: a.curve === 'hold' });
    }
    // Trail: flat hold from last marker to end.
    const last = pts[pts.length - 1]!;
    const lastX = pxAt(last.position, props.pxPerBar);
    if (lastX < width()) {
      const c = rgbToCss(colorMarker01ToU8(last.color));
      out.push({ leftPx: lastX, widthPx: width() - lastX, from: c, to: c, hold: true });
    }
    return out;
  });
  return (
    <div class="timeline-color-lane-strip" aria-hidden="true">
      <For each={segments()}>
        {(seg) => (
          <div
            class="timeline-color-lane-segment"
            style={{
              left: `${seg.leftPx}px`,
              width: `${seg.widthPx}px`,
              background: seg.hold
                ? seg.from
                : `linear-gradient(to right, ${seg.from}, ${seg.to})`,
            }}
          />
        )}
      </For>
    </div>
  );
}

function colorMarker01ToU8(c: { r: number; g: number; b: number; w?: number }): { r: number; g: number; b: number } {
  const clip = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255)));
  return {
    r: clip((c.r ?? 0) + (c.w ?? 0)),
    g: clip((c.g ?? 0) + (c.w ?? 0)),
    b: clip((c.b ?? 0) + (c.w ?? 0)),
  };
}

// =====================================================================
// COLOR MARKER CHIP — diamond block sitting on the color lane at the
// marker's beat, filled with the marker's authored color.
// =====================================================================

function ColorMarkerChip(props: {
  laneId: string;
  step: ColorMarkerStep;
  pxPerBar: number;
  sigNum: number;
  /** Why it won't play, or null (plan 3.3). */
  wontPlay: string | null;
}) {
  const bg = () => rgbToCss(colorMarker01ToU8(props.step.color));
  const selected = () => isSelected(props.laneId, props.step.id);
  // Beat-wide block, aligned to the grid: left edge sits AT the marker's
  // beat, width = one beat. Uses the same end-minus-start px math as the
  // grid ticks so adjacent markers butt exactly.
  const leftPx = () => pxAt(props.step.position, props.pxPerBar);
  const widthPx = () =>
    pxAt(props.step.position + 1 / Math.max(1, props.sigNum), props.pxPerBar) - leftPx();
  return (
    <div
      class="timeline-color-marker"
      data-step-id={props.step.id}
      data-selected={selected() ? 'true' : 'false'}
      data-curve={props.step.curve ?? 'linear'}
      data-wont-play={props.wontPlay ? 'true' : undefined}
      style={{
        left: `${leftPx()}px`,
        width: `${widthPx()}px`,
        background: bg(),
      }}
      title={`${props.wontPlay ? `Won't play yet: ${props.wontPlay}.\n` : ''}${props.step.label ?? 'Color'} @ bar ${(props.step.position + 1).toFixed(2)} - right-click for menu, double-click to delete`}
    />
  );
}

// =====================================================================
// COLOR MARKER MENU — right-click on a marker: pick / curve / delete.
// =====================================================================

const CURVE_OPTIONS: Array<{ id: ValuePointCurve; label: string }> = [
  { id: 'linear',      label: 'Linear' },
  { id: 'hold',        label: 'Hold' },
  { id: 'ease-in',     label: 'Ease in' },
  { id: 'ease-out',    label: 'Ease out' },
  { id: 'ease-in-out', label: 'Ease in-out' },
];

/** Interpolation-space picker options for color markers. Order matches
 *  operator ergonomics: sRGB first (legacy/default), HSL next (stage-
 *  lighting familiar), OKLCH last (perceptually best, modern). */
const SPACE_OPTIONS: Array<{ id: ColorInterpolationSpace; label: string }> = [
  { id: 'sRGB',  label: 'sRGB (linear channels)' },
  { id: 'HSL',   label: 'HSL (hue wheel)' },
  { id: 'OKLCH', label: 'OKLCH (perceptual)' },
];

const PATH_OPTIONS: Array<{ id: HuePathDirection; label: string }> = [
  { id: 'short', label: 'Short (natural fade)' },
  { id: 'long',  label: 'Long (rainbow sweep)' },
];

function KeyframeMenu(props: {
  x: number;
  y: number;
  /** Only show "Pick color…" when the target is a color marker. */
  showPickColor: boolean;
  /** Show the Curve section. Only meaningful for colorMarker keyframes —
   *  step blocks (buttonRef, cue, marker, animation, sequencer, macro)
   *  don't ramp, so Curve is noise there. */
  showCurve: boolean;
  /** Show the Space + Path pickers. Only useful for color markers. */
  showColorInterpolation: boolean;
  /** Reactive — clipboard state. When false the Paste item is greyed. */
  hasClipboard: boolean;
  onPickColor: () => void;
  onCurve: (c: ValuePointCurve) => void;
  onSpace: (s: ColorInterpolationSpace) => void;
  onPath: (p: HuePathDirection) => void;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onDismiss: () => void;
}) {
  return (
    <Portal>
      <div
        class="timeline-color-menu-backdrop"
        onClick={props.onDismiss}
        onContextMenu={(ev) => { ev.preventDefault(); props.onDismiss(); }}
      />
      <div
        class="timeline-color-menu"
        style={{ top: `${props.y + 4}px`, left: `${props.x + 16}px` }}
        onClick={(ev) => ev.stopPropagation()}
        onPointerDown={(ev) => ev.stopPropagation()}
        onPointerUp={(ev) => ev.stopPropagation()}
      >
        <Show when={props.showPickColor}>
          <button type="button" class="timeline-color-menu-item" onClick={props.onPickColor}>
            Pick color…
          </button>
        </Show>
        <Show when={props.showCurve}>
          <div class="timeline-color-menu-sep" aria-hidden="true">Curve</div>
          <For each={CURVE_OPTIONS}>
            {(opt) => (
              <button
                type="button"
                class="timeline-color-menu-item"
                onClick={() => props.onCurve(opt.id)}
              >
                {opt.label}
              </button>
            )}
          </For>
        </Show>
        <Show when={props.showColorInterpolation}>
          <div class="timeline-color-menu-sep" aria-hidden="true">Space</div>
          <For each={SPACE_OPTIONS}>
            {(opt) => (
              <button
                type="button"
                class="timeline-color-menu-item"
                onClick={() => props.onSpace(opt.id)}
              >
                {opt.label}
              </button>
            )}
          </For>
          <div class="timeline-color-menu-sep" aria-hidden="true">Hue path</div>
          <For each={PATH_OPTIONS}>
            {(opt) => (
              <button
                type="button"
                class="timeline-color-menu-item"
                onClick={() => props.onPath(opt.id)}
              >
                {opt.label}
              </button>
            )}
          </For>
        </Show>
        <div class="timeline-color-menu-sep" aria-hidden="true" />
        <button type="button" class="timeline-color-menu-item" onClick={props.onCopy}>
          Copy <span class="timeline-color-menu-shortcut">Ctrl+C</span>
        </button>
        <button type="button" class="timeline-color-menu-item" onClick={props.onCut}>
          Cut <span class="timeline-color-menu-shortcut">Ctrl+X</span>
        </button>
        <button
          type="button"
          class="timeline-color-menu-item"
          disabled={!props.hasClipboard}
          data-disabled={props.hasClipboard ? 'false' : 'true'}
          onClick={props.onPaste}
        >
          Paste <span class="timeline-color-menu-shortcut">Ctrl+V</span>
        </button>
        <button
          type="button"
          class="timeline-color-menu-item"
          onClick={props.onDuplicate}
        >
          Duplicate <span class="timeline-color-menu-shortcut">Ctrl+D</span>
        </button>
        <div class="timeline-color-menu-sep" aria-hidden="true" />
        <button
          type="button"
          class="timeline-color-menu-item"
          data-destructive="true"
          onClick={props.onDelete}
        >
          Delete <span class="timeline-color-menu-shortcut">Del</span>
        </button>
      </div>
    </Portal>
  );
}

// =====================================================================
// EMPTY-LANE PASTE MENU — the right-click affordance for pasting the
// clipboard at a specific click position on an otherwise-empty lane.
// Same visual as KeyframeMenu but with only the Paste entry. Grayed
// when clipboard is empty.
// =====================================================================

function EmptyLanePasteMenu(props: {
  x: number;
  y: number;
  hasClipboard: boolean;
  onPaste: () => void;
  onDismiss: () => void;
}) {
  return (
    <Portal>
      <div
        class="timeline-color-menu-backdrop"
        onClick={props.onDismiss}
        onContextMenu={(ev) => { ev.preventDefault(); props.onDismiss(); }}
      />
      <div
        class="timeline-color-menu"
        style={{ top: `${props.y + 4}px`, left: `${props.x + 16}px` }}
        onClick={(ev) => ev.stopPropagation()}
        onPointerDown={(ev) => ev.stopPropagation()}
        onPointerUp={(ev) => ev.stopPropagation()}
      >
        <button
          type="button"
          class="timeline-color-menu-item"
          disabled={!props.hasClipboard}
          data-disabled={props.hasClipboard ? 'false' : 'true'}
          onClick={props.onPaste}
        >
          Paste here <span class="timeline-color-menu-shortcut">Ctrl+V</span>
        </button>
      </div>
    </Portal>
  );
}

// =====================================================================
// COLOR PICKER — HSV wheel + brightness + presets + hex input. Live-
// updates the marker as the user drags. Dismiss = commit.
// =====================================================================

/** Descriptive lighting presets — gel-inspired but plainly named so a
 *  scanning operator can find them fast. */
const COLOR_PRESETS: Array<{ label: string; rgb: { r: number; g: number; b: number } }> = [
  { label: 'Off',           rgb: { r: 0.00, g: 0.00, b: 0.00 } },
  { label: 'White',         rgb: { r: 1.00, g: 1.00, b: 1.00 } },
  { label: 'Warm White',    rgb: { r: 1.00, g: 0.84, b: 0.64 } },
  { label: 'Cool White',    rgb: { r: 0.81, g: 0.90, b: 1.00 } },
  { label: 'Red',           rgb: { r: 1.00, g: 0.00, b: 0.00 } },
  { label: 'Deep Red',      rgb: { r: 0.55, g: 0.00, b: 0.00 } },
  { label: 'Amber',         rgb: { r: 1.00, g: 0.69, b: 0.00 } },
  { label: 'Yellow',        rgb: { r: 1.00, g: 1.00, b: 0.00 } },
  { label: 'Green',         rgb: { r: 0.00, g: 1.00, b: 0.00 } },
  { label: 'Teal',          rgb: { r: 0.00, g: 0.80, b: 0.80 } },
  { label: 'Cyan',          rgb: { r: 0.00, g: 1.00, b: 1.00 } },
  { label: 'Sky Blue',      rgb: { r: 0.40, g: 0.75, b: 1.00 } },
  { label: 'Blue',          rgb: { r: 0.00, g: 0.20, b: 1.00 } },
  { label: 'Deep Blue',     rgb: { r: 0.00, g: 0.00, b: 0.60 } },
  { label: 'Purple',        rgb: { r: 0.50, g: 0.00, b: 1.00 } },
  { label: 'Magenta',       rgb: { r: 1.00, g: 0.00, b: 1.00 } },
  { label: 'Pink',          rgb: { r: 1.00, g: 0.55, b: 0.75 } },
  { label: 'UV',            rgb: { r: 0.30, g: 0.00, b: 0.60 } },
];

function ColorPicker(props: {
  x: number;
  y: number;
  initial: { r: number; g: number; b: number; w?: number };
  onChange: (rgb: { r: number; g: number; b: number }) => void;
  onDismiss: () => void;
}) {
  const [r, setR] = createSignal(props.initial.r);
  const [g, setG] = createSignal(props.initial.g);
  const [b, setB] = createSignal(props.initial.b);

  function commit(rr: number, gg: number, bb: number) {
    setR(rr); setG(gg); setB(bb);
    props.onChange({ r: rr, g: gg, b: bb });
  }
  function pickPreset(p: { r: number; g: number; b: number }) {
    commit(p.r, p.g, p.b);
  }
  function onSlider(ev: InputEvent, ch: 'r' | 'g' | 'b') {
    const v = Number((ev.currentTarget as HTMLInputElement).value) / 255;
    if (ch === 'r') commit(v, g(), b());
    if (ch === 'g') commit(r(), v, b());
    if (ch === 'b') commit(r(), g(), v);
  }
  function onHexInput(ev: InputEvent) {
    const raw = (ev.currentTarget as HTMLInputElement).value.trim();
    const m = /^#?([0-9a-fA-F]{6})$/.exec(raw);
    if (!m) return;
    const int = Number.parseInt(m[1]!, 16);
    const rr = ((int >> 16) & 0xff) / 255;
    const gg = ((int >> 8) & 0xff) / 255;
    const bb = (int & 0xff) / 255;
    commit(rr, gg, bb);
  }
  const currentHex = () => {
    const to2 = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
    return `#${to2(r())}${to2(g())}${to2(b())}`;
  };
  const swatch = () => rgbToCss(colorMarker01ToU8({ r: r(), g: g(), b: b() }));

  return (
    <Portal>
      <div
        class="timeline-color-picker-backdrop"
        onClick={props.onDismiss}
        onContextMenu={(ev) => { ev.preventDefault(); props.onDismiss(); }}
      />
      <div
        class="timeline-color-picker"
        style={{ top: `${props.y}px`, left: `${props.x}px` }}
        onClick={(ev) => ev.stopPropagation()}
        role="dialog"
        aria-label="Color picker"
      >
        <div class="timeline-color-picker-swatch" style={{ background: swatch() }} />
        <div class="timeline-color-picker-presets">
          <For each={COLOR_PRESETS}>
            {(p) => (
              <button
                type="button"
                class="timeline-color-picker-preset"
                style={{ background: rgbToCss(colorMarker01ToU8(p.rgb)) }}
                title={p.label}
                onClick={() => pickPreset(p.rgb)}
                aria-label={p.label}
              />
            )}
          </For>
        </div>
        <div class="timeline-color-picker-sliders">
          <label class="timeline-color-picker-slider" data-ch="r">
            R
            <input type="range" min={0} max={255} step={1}
              value={Math.round(r() * 255)}
              onInput={(ev) => onSlider(ev as unknown as InputEvent, 'r')} />
            <span>{Math.round(r() * 255)}</span>
          </label>
          <label class="timeline-color-picker-slider" data-ch="g">
            G
            <input type="range" min={0} max={255} step={1}
              value={Math.round(g() * 255)}
              onInput={(ev) => onSlider(ev as unknown as InputEvent, 'g')} />
            <span>{Math.round(g() * 255)}</span>
          </label>
          <label class="timeline-color-picker-slider" data-ch="b">
            B
            <input type="range" min={0} max={255} step={1}
              value={Math.round(b() * 255)}
              onInput={(ev) => onSlider(ev as unknown as InputEvent, 'b')} />
            <span>{Math.round(b() * 255)}</span>
          </label>
        </div>
        <label class="timeline-color-picker-hex">
          Hex
          <input
            type="text"
            spellcheck={false}
            value={currentHex()}
            onInput={(ev) => onHexInput(ev as unknown as InputEvent)}
          />
        </label>
        <button
          type="button"
          class="timeline-color-picker-done"
          onClick={props.onDismiss}
        >
          Done
        </button>
      </div>
    </Portal>
  );
}

interface BeatGridProps {
  totalBars: number;
  pxPerBar: number;
  sigNum: number;
}

function BeatGrid(props: BeatGridProps) {
  const marks = createMemo(() => {
    const out: Array<{ pos: number; strong: boolean }> = [];
    const beatsPerBar = Math.max(1, props.sigNum);
    const totalBeats = props.totalBars * beatsPerBar;
    for (let b = 0; b <= totalBeats; b++) {
      out.push({ pos: b / beatsPerBar, strong: b % beatsPerBar === 0 });
    }
    return out;
  });
  return (
    <div class="timeline-beat-grid" aria-hidden="true">
      <For each={marks()}>
        {(m) => (
          <div
            class="timeline-beat-tick"
            data-strong={m.strong ? 'true' : 'false'}
            style={{ left: `${pxAt(m.pos, props.pxPerBar)}px` }}
          />
        )}
      </For>
    </div>
  );
}

interface StepBlockProps {
  timelineId: string;
  laneId: string;
  step: TimelineStep;
  pxPerBar: number;
  sigNum: number;
  /** Timeline length in bars. Right-edge resize + body-drag clamp against
   *  this so steps can't extend past the end of the timeline. */
  totalBars: number;
  selected: boolean;
  onSelect: () => void;
  onContextMenu: (ev: MouseEvent) => void;
  /** Why this step won't play where the timeline plays, or null (plan 3.3).
   *  Drawn as a dashed block with a "⊘" before the label - the editor's "not
   *  solid yet" (DEC-232 rule 3) - and spelled out in the tooltip. */
  wontPlay: string | null;
}

const CLICK_DRAG_THRESHOLD_PX = 4;

/** Snap a fractional-bar position to the nearest beat. Same rule as
 *  palette drops. */
function snapPos(positionBars: number, sigNum: number): number {
  return snapToBeat(positionBars, sigNum);
}

/** Snap a duration (in bars) to the nearest beat. Minimum one beat. */
function snapDuration(durationBars: number, sigNum: number): number {
  const beats = Math.max(1, Math.round(durationBars * sigNum));
  return beats / sigNum;
}

/** A buttonRef step's referenced item's own author-assigned accent colour,
 *  resolved through `hueOf()` (DEC-127's `hue` axis, falling back to the
 *  retired `color` field for items that never picked one) — NOT the raw
 *  legacy `color` field alone, which misses anything authored through the
 *  current Inspector colour picker (2026-08-18, operator ask: "carry the
 *  colour properties over from canvas to palette AND timeline"; the
 *  placed step block didn't carry any colour at all before this, only its
 *  step KIND). Live lookup, not cached like `labelHint` — if the item's
 *  colour changes after the step was placed, the block should follow it.
 *  Searches every page, same reach as `Palette()`'s own item lookup.
 *  `undefined` for a dangling reference, a non-fireable-item id, or an
 *  item with no colour picked at all (hue `'none'`) — all three fall
 *  through to the existing kind-based spine default. */
function paletteItemHueHex(buttonId: string): string | undefined {
  for (const p of pagesOfActive()) {
    const item = p.items.find((i) => i.id === buttonId);
    if (item && isPaletteFireableItem(item)) {
      const hue = hueOf(item);
      return hue === 'none' ? undefined : HUE_VALUES[hue];
    }
  }
  return undefined;
}

/** Walk up from `el` looking for `.timeline-lane-track`. Returns the
 *  track element + its parsed `data-lane-id` or null. */
function findLaneAt(el: Element | null): { track: HTMLElement; laneId: string } | null {
  const track = el?.closest('.timeline-lane-track') as HTMLElement | null;
  if (!track) return null;
  const laneId = track.dataset.laneId;
  if (!laneId) return null;
  return { track, laneId };
}

function StepBlock(props: StepBlockProps) {
  /** buttonRef steps only — every other step kind keeps its existing
   *  kind-based spine colour (see skin-components.css). */
  const itemHue = () => (isButtonRefStep(props.step) ? paletteItemHueHex(props.step.buttonId) : undefined);
  const label = () => {
    const s = props.step;
    if (isButtonRefStep(s)) return s.labelHint ?? 'item';
    if (isTargetValueStep(s)) {
      return s.labelHint ?? `${getLiveTarget(s.targetId)?.label ?? s.targetId}: ${s.value}`;
    }
    if (s.kind === 'marker') return `▲ ${s.text ?? 'marker'}`;
    if (s.kind === 'animation') return s.preset ?? 'anim';
    if (s.kind === 'sequencer') return 'seq';
    if (s.kind === 'macro') return 'macro';
    return 'cue';
  };
  /** Steps that carry an authored `durationBars` field. All others
   *  render as instant-fire (one-beat visual anchor). */
  const isDurational = () => props.step.kind === 'animation' || props.step.kind === 'buttonRef';
  const oneBeatPx = () => props.pxPerBar / Math.max(1, props.sigNum);
  const width = () => {
    const s = props.step;
    if ((s.kind === 'animation' || s.kind === 'buttonRef') && typeof s.durationBars === 'number') {
      return Math.max(6, s.durationBars * props.pxPerBar);
    }
    // Instant-fire steps (cue / macro / sequencer / marker + un-resized
    // buttonRef) render as one beat wide so they lock to the beat grid.
    return oneBeatPx();
  };

  const [dragging, setDragging] = createSignal<null | { rejectTarget: boolean }>(null);
  const [livePosition, setLivePosition] = createSignal<number | null>(null);
  const [liveDuration, setLiveDuration] = createSignal<number | null>(null);

  const effectivePosition = () => livePosition() ?? props.step.position;
  const effectiveWidth = () => {
    const s = props.step;
    if (s.kind === 'animation' || s.kind === 'buttonRef') {
      const stored = typeof s.durationBars === 'number' ? s.durationBars : null;
      const dur = liveDuration() ?? stored;
      if (dur !== null) return Math.max(6, dur * props.pxPerBar);
    }
    return width();
  };

  /** MOVE — pointer-drag on body (not on edge handles). Below threshold
   *  the pointerup selects; above, we cross-lane-drag + horizontal-snap. */
  function onBodyPointerDown(ev: PointerEvent) {
    // Ignore right-click, resize handles, keyboard modifier drags, etc.
    if (ev.button !== 0) return;
    const target = ev.target as HTMLElement | null;
    if (target?.closest('.timeline-step-resize')) return;
    ev.preventDefault();
    ev.stopPropagation();

    const startX = ev.clientX;
    const startY = ev.clientY;
    const originPosition = props.step.position;
    // Grab-point offset within the block so the block doesn't jump to the
    // cursor's edge on drag-start.
    const blockRect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const grabOffsetPx = ev.clientX - blockRect.left;

    let started = false;
    let currentLaneId = props.laneId;
    let currentReject = false;

    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!started) {
        if (Math.abs(dx) <= CLICK_DRAG_THRESHOLD_PX && Math.abs(dy) <= CLICK_DRAG_THRESHOLD_PX) return;
        started = true;
      }

      // Vertical: cross-lane detection via elementFromPoint at the cursor.
      const hit = findLaneAt(document.elementFromPoint(e.clientX, e.clientY));
      if (hit) {
        currentLaneId = hit.laneId;
        const destKind = (hit.track.dataset.kind ?? 'mixed') as TimelineLaneKind;
        const stepKind = props.step.kind as NonNullable<PaletteDragPayload['stepKind']>;
        currentReject = !laneAcceptsStep(destKind, stepKind);
      } else {
        currentReject = true;
      }

      // Horizontal: convert the current cursor x to a lane-local x, then
      // to bars, then snap to beat.
      const trackEl = hit?.track;
      const trackRect = trackEl?.getBoundingClientRect();
      let rawBars: number;
      if (trackRect) {
        const localX = e.clientX - trackRect.left - grabOffsetPx;
        rawBars = localX / props.pxPerBar;
      } else {
        // No lane under cursor — extrapolate from origin.
        rawBars = originPosition + dx / props.pxPerBar;
      }
      // Cap at the timeline's right edge: the step's right side can't
      // extend past totalBars. For instant-fire steps (no duration) the
      // block still has a visual footprint of 1 beat, so clamp against
      // that too.
      const stepDur = typeof (props.step as { durationBars?: number }).durationBars === 'number'
        ? (props.step as { durationBars?: number }).durationBars!
        : 1 / props.sigNum;
      const maxPos = Math.max(0, props.totalBars - stepDur);
      const snapped = snapPos(Math.max(0, Math.min(maxPos, rawBars)), props.sigNum);
      setLivePosition(snapped);
      setDragging({ rejectTarget: currentReject });
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);

      if (!started) {
        // Click, not drag — just select.
        props.onSelect();
        setDragging(null);
        setLivePosition(null);
        return;
      }

      const finalPos = livePosition() ?? originPosition;
      if (currentReject) {
        // Snap back — the drop target refused this step kind.
        setDragging(null);
        setLivePosition(null);
        return;
      }
      // The moved block wins: whatever it now covers is cut away (2026-09-29,
      // operator: "i presumed it would overwrite the section of the block
      // that it covered"). Across lanes, moveStepToLane does the same.
      if (currentLaneId !== props.laneId) {
        moveStepToLane(props.timelineId, props.laneId, currentLaneId, props.step.id, finalPos);
      } else if (finalPos !== originPosition) {
        placeStep(props.timelineId, props.laneId, props.step.id, { position: finalPos });
      }
      setDragging(null);
      setLivePosition(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /** RESIZE — left edge. Adjusts position + duration inversely (dragging
   *  the left edge right shortens the step and moves its start; dragging
   *  left extends the step and moves its start left). Animation +
   *  buttonRef. */
  function onResizeLeftPointerDown(ev: PointerEvent) {
    if (ev.button !== 0) return;
    if (props.step.kind !== 'animation' && props.step.kind !== 'buttonRef') return;
    ev.preventDefault();
    ev.stopPropagation();

    const startX = ev.clientX;
    const originPos = props.step.position;
    // buttonRef un-resized renders as 1 beat visually; use that as the
    // starting duration reference so the right edge sits where you see it.
    const storedDur = typeof (props.step as { durationBars?: number }).durationBars === 'number'
      ? (props.step as { durationBars?: number }).durationBars!
      : 1 / props.sigNum;
    const originDur = storedDur;
    const rightEdge = originPos + originDur;

    const onMove = (e: PointerEvent) => {
      const dxBars = (e.clientX - startX) / props.pxPerBar;
      const newPosRaw = originPos + dxBars;
      const snappedPos = snapPos(Math.max(0, Math.min(rightEdge - 1 / props.sigNum, newPosRaw)), props.sigNum);
      const snappedDur = snapDuration(rightEdge - snappedPos, props.sigNum);
      setLivePosition(snappedPos);
      setLiveDuration(snappedDur);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const finalPos = livePosition();
      const finalDur = liveDuration();
      // One edit (one undo step), then the resized block wins over what it
      // now covers. Two separate calls used to leave an in-between state
      // (new start, OLD length) that could cut a block the final one misses.
      const change: { position?: number; durationBars?: number } = {};
      if (finalPos !== null && finalPos !== originPos) change.position = finalPos;
      if (finalDur !== null && finalDur !== originDur) change.durationBars = finalDur;
      if (change.position !== undefined || change.durationBars !== undefined) {
        placeStep(props.timelineId, props.laneId, props.step.id, change);
      }
      setLivePosition(null);
      setLiveDuration(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /** RESIZE — right edge. Adjusts duration only. Animation + buttonRef. */
  function onResizeRightPointerDown(ev: PointerEvent) {
    if (ev.button !== 0) return;
    if (props.step.kind !== 'animation' && props.step.kind !== 'buttonRef') return;
    ev.preventDefault();
    ev.stopPropagation();

    const startX = ev.clientX;
    const storedDur = typeof (props.step as { durationBars?: number }).durationBars === 'number'
      ? (props.step as { durationBars?: number }).durationBars!
      : 1 / props.sigNum;
    const originDur = storedDur;

    const onMove = (e: PointerEvent) => {
      const dxBars = (e.clientX - startX) / props.pxPerBar;
      const minDur = 1 / props.sigNum;
      // Cap at the timeline's right edge: the step's right side can't
      // extend past totalBars. maxDur floors at minDur so a step already
      // at/past the edge can still be dragged (and re-clamped) without
      // collapsing.
      const maxDur = Math.max(minDur, props.totalBars - props.step.position);
      const rawDur = Math.max(minDur, Math.min(maxDur, originDur + dxBars));
      const snapped = snapDuration(rawDur, props.sigNum);
      setLiveDuration(snapped);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const finalDur = liveDuration();
      if (finalDur !== null && finalDur !== originDur) {
        placeStep(props.timelineId, props.laneId, props.step.id, { durationBars: finalDur });
      }
      setLiveDuration(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  return (
    <div
      class="timeline-step-block"
      data-kind={props.step.kind}
      data-step-id={props.step.id}
      data-selected={props.selected ? 'true' : 'false'}
      data-dragging={dragging() ? 'true' : 'false'}
      data-reject={dragging()?.rejectTarget ? 'true' : 'false'}
      data-wont-play={props.wontPlay ? 'true' : undefined}
      style={{
        left: `${pxAt(effectivePosition(), props.pxPerBar)}px`,
        width: `${Math.round(effectiveWidth())}px`,
        ...(itemHue() ? { '--item-hue': itemHue() } : {}),
      }}
      onPointerDown={onBodyPointerDown}
      onContextMenu={props.onContextMenu}
      title={`${props.wontPlay ? `Won't play yet: ${props.wontPlay}.\n` : ''}${label()} @ bar ${(effectivePosition() + 1).toFixed(2)} - drag to move, right-click to remove`}
    >
      {/* Left-edge resize grabber (animation + buttonRef). */}
      <Show when={isDurational()}>
        <div
          class="timeline-step-resize timeline-step-resize-left"
          onPointerDown={onResizeLeftPointerDown}
          aria-label="Resize step (left edge)"
        />
      </Show>
      <span class="timeline-step-block-label">
        <Show when={props.wontPlay}>
          <span class="timeline-step-wont-play" aria-label="won't play yet">⊘ </span>
        </Show>
        {label()}
      </span>
      {/* Right-edge resize grabber (animation + buttonRef). */}
      <Show when={isDurational()}>
        <div
          class="timeline-step-resize timeline-step-resize-right"
          onPointerDown={onResizeRightPointerDown}
          aria-label="Resize step (right edge)"
        />
      </Show>
    </div>
  );
}

// =====================================================================
// SECTIONS EDITOR (Song/Genre) + ACTION LENGTH EDITOR
// =====================================================================

/** Preserve caret position across a controlled-input reactive rewrite.
 *  Solid's `value={reactive}` binding writes to input.value on every store
 *  update, which resets the caret to the end. This helper stashes the
 *  caret before the store call and restores it after the DOM update. */
function preserveCaret(ev: InputEvent, apply: (value: string) => void) {
  const input = ev.currentTarget as HTMLInputElement;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  apply(input.value);
  queueMicrotask(() => {
    if (document.activeElement === input && start !== null && end !== null) {
      try {
        input.setSelectionRange(start, end);
      } catch {
        // Some input types don't support selection APIs (number, etc.).
      }
    }
  });
}

function SectionsEditor(props: { timeline: Timeline }) {
  const t = () => props.timeline;
  return (
    <div class="timeline-editor-sections-list">
      <header class="timeline-editor-sections-head">
        <span class="timeline-editor-sections-title">Sections</span>
        <span class="timeline-editor-sections-total">Total: {totalBars(t())} bars</span>
        <button
          type="button"
          class="timeline-editor-sections-add"
          onClick={() => addSection(t().id)}
        >
          + Add section
        </button>
      </header>
      <ul class="timeline-editor-sections-rows">
        <Index each={t().sections}>
          {(s, i) => (
            <li class="timeline-editor-section-row">
              <span class="timeline-editor-section-num">{i + 1}</span>
              <input
                class="timeline-editor-section-name"
                type="text"
                value={s().name}
                onFocus={(ev) => {
                  (ev.currentTarget as HTMLInputElement).dataset.focusValue = s().name;
                }}
                onInput={(ev) =>
                  preserveCaret(ev, (v) => renameSection(t().id, s().id, v))
                }
                onBlur={(ev) => {
                  const prev = (ev.currentTarget as HTMLInputElement).dataset.focusValue ?? '';
                  commitSectionName(t().id, s().id, prev);
                }}
                onKeyDown={(ev) => {
                  const input = ev.currentTarget as HTMLInputElement;
                  if (ev.key === 'Enter') {
                    input.blur();
                  } else if (ev.key === 'Escape') {
                    ev.stopPropagation();
                    const prev = input.dataset.focusValue ?? '';
                    renameSection(t().id, s().id, prev);
                    input.blur();
                  }
                }}
                spellcheck={false}
                aria-label={`Section ${i + 1} name`}
              />
              <label class="timeline-editor-section-len">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={s().lengthBars}
                  onChange={(ev) => {
                    const v = Number.parseInt((ev.currentTarget as HTMLInputElement).value, 10);
                    if (Number.isFinite(v)) setSectionLength(t().id, s().id, v);
                  }}
                  aria-label={`Section ${i + 1} length in bars`}
                />
                <span>bars</span>
              </label>
              <button
                type="button"
                class="timeline-editor-section-remove"
                disabled={t().sections.length <= 1}
                onClick={() => removeSection(t().id, s().id)}
                title={t().sections.length <= 1 ? 'Every timeline needs at least one section' : 'Remove section'}
                aria-label="Remove section"
              >
                ×
              </button>
            </li>
          )}
        </Index>
      </ul>
    </div>
  );
}

function ActionLengthEditor(props: { timeline: Timeline }) {
  const t = () => props.timeline;
  const only = () => t().sections[0];
  return (
    <div class="timeline-editor-action-len">
      <label>
        Length
        <input
          type="number"
          min={1}
          step={1}
          value={only()?.lengthBars ?? 1}
          onChange={(ev) => {
            const v = Number.parseInt((ev.currentTarget as HTMLInputElement).value, 10);
            const s = only();
            if (s && Number.isFinite(v)) setSectionLength(t().id, s.id, v);
          }}
        />
        <span>bars</span>
      </label>
      <span class="timeline-editor-action-hint">Action Timelines are one-shot chains - sections don't apply here.</span>
    </div>
  );
}

// =====================================================================
// FIXTURE PICKER — modal for selecting a fixture to add to the timeline
// =====================================================================

function FixturePicker(props: { onPick: (fixtureId: string) => void; onCancel: () => void }) {
  const list = fixtures;
  return (
    <Portal>
      <div class="fixture-picker-overlay" role="dialog" aria-modal="true" aria-label="Add lighting fixture">
        <div class="fixture-picker">
          <header class="fixture-picker-head">
            <span class="fixture-picker-title">ADD LIGHTING FIXTURE</span>
            <button
              type="button"
              class="fixture-picker-close"
              onClick={props.onCancel}
              aria-label="Cancel"
            >
              ×
            </button>
          </header>
          <p class="fixture-picker-hint">
            Pick a fixture from your rig to add a DMX Fixture lane for it.
          </p>
          <Show
            when={list().length > 0}
            fallback={
              <p class="fixture-picker-empty">
                No fixtures yet. A default PAR CAN is seeded on first launch; a full "Add fixture" settings page is coming.
              </p>
            }
          >
            <ul class="fixture-picker-list">
              <For each={list()}>
                {(f: FixtureInstance) => (
                  <li>
                    <button
                      type="button"
                      class="fixture-picker-item"
                      onClick={() => props.onPick(f.id)}
                    >
                      <span class="fixture-picker-item-name">{f.name}</span>
                      <span class="fixture-picker-item-meta">
                        {f.profileId} · U{f.universe} · addr {f.dmxAddress}
                      </span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </div>
    </Portal>
  );
}
